import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import "../MesrequetesStyles/DemandeConges.css";
import {
    Calendar,
    Clock,
    Plus,
    FileText,
    AlertCircle,
    CheckCircle,
    XCircle,
    Upload,
    X,
    Eye,
    FileText as FileTextIcon,
    TrendingUp,
    Award,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    ChevronDown,
} from "lucide-react";


// ✅ Leave requests (mine + create)
import {
    getMyLeaveRequestsRequest,
    createLeaveRequestRequest,
    clearLeaveRequestMessage,
    postponeLeaveRequestRequest,
} from "../../../../redux/actions/leaveRequestsActions";

import {
    getWorkdaysConfigRequest,
    getCalendarSettingsRequest,
} from "../../../../redux/actions/workDaysActions";

// ✅ Param congés (typesAbsences + justificatifs)
import { fetchParamCongesRequest } from "../../../../redux/actions/paramCongesActions";

// ✅ Balance (soldes)
import {
    getUserSoldesRequest,
    checkBalanceRequest,
    cancelLeaveRequestRequest,
    getUserMovementsRequest,
    getAllMovementsRequest,
} from "../../../../redux/actions/leaveBalanceActions";

import { API_BASE_URL } from "../../../../../api";
import { setAuthenticatedUser } from "../../../../redux/actions/authActions";
import { getAllUsersRequest } from "../../../../redux/actions/userActions";
import { getAllEntitesRequest } from "../../../../redux/actions/entitesActions";
import useRequestHistoryAutoRefresh from "./useRequestHistoryAutoRefresh";
import { getRequestStatusLabel, getRequestTracking } from "./requestWorkflowTracking";



const DemandeConges = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();

    // ===================== REDUX: leave requests =====================
    const leaveState = useSelector((state) => state.leaveRequests || state.leaveRequest || {});
    const myRequests = leaveState.myRequests || [];
    const loading = !!leaveState.loading;
    const apiError = leaveState.error || "";
    const apiMessage = leaveState.message || "";

    // Extraction des jours de travail configurés
    const { workdaysConfig, calendarSettings } = useSelector((state) => state.workDays || {});

    // ===================== REDUX: param congés =====================
    const paramState = useSelector((state) => state.paramConges || {});
    const typesAbsences = paramState.typesAbsences || [];

    const paramLoading = !!paramState.loading;

    // ===================== REDUX: balance (soldes) =====================
    const balanceState = useSelector((state) => state.leaveBalance || {});
    const userSoldes = balanceState.userSoldes || [];
    // Ne jamais utiliser l'historique global RH ici : il est partagé dans le
    // store avec SoldeCongesManagement et peut contenir d'autres collaborateurs.
    const movements = balanceState.userMovements || [];
    const balanceLoading = !!balanceState.loading;
    const balanceCheck = balanceState.balanceCheck || null;

    // ===================== REDUX: user info =====================
    const authState = useSelector((state) => state.auth || {});
    const currentUser = authState.user || authState.currentUser || {};
    const companyUsers = useSelector((state) => state.user?.users || []);
    const entites = useSelector((state) => state.entite?.entites || []);
    const userId = currentUser.id || sessionStorage.getItem("uid");
    // Après un rechargement, l'entreprise sélectionnée est stockée sous
    // `selectedCompanyId`. Sans ce repli, le chargement des soldes est ignoré
    // et l'interface affiche 0 alors qu'un solde existe en base.
    const companyId = currentUser.companyId || sessionStorage.getItem("selectedCompanyId") || sessionStorage.getItem("companyId");

    // ===================== UI STATE =====================
    const [activeTab, setActiveTab] = useState("solde");
    const [showDemandeModal, setShowDemandeModal] = useState(false);
    const [showJustificatifModal, setShowJustificatifModal] = useState(false);
    const [showAnnulationModal, setShowAnnulationModal] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [soldeError, setSoldeError] = useState("");
    const [selectedHistoryDate, setSelectedHistoryDate] = useState("");
    const [closedLeaveFolders, setClosedLeaveFolders] = useState(new Set());
    const [replacementTasks, setReplacementTasks] = useState([]);
    const [replacementTasksLoading, setReplacementTasksLoading] = useState(false);
    const [replacementHistory, setReplacementHistory] = useState([]);
    const [replacementHistoryFor, setReplacementHistoryFor] = useState(null);

    // ===================== COLLABORATEUR (TEMP) =====================
    const collaborateur = {
        matricule: currentUser.employeeCode || "EMP004",
        nom: currentUser.fullName || "Utilisateur",
        poste: currentUser.position || "",
        departement: currentUser.department || "",
    };

    // Les mêmes paramètres de calendrier que ParamJT : aucune valeur locale
    // n'est figée afin que les collaborateurs voient les réglages RH à jour.
    const fixedHolidays = useMemo(() => (calendarSettings?.fixed || []).map((holiday) => ({
        date: holiday.key,
        description: holiday.label,
        selected: !!holiday.selected,
    })), [calendarSettings]);

    const hijriHolidays = useMemo(() => (calendarSettings?.hijri || []).map((holiday) => ({
        name: holiday.name,
        date: holiday.date || "",
        duration: Number(holiday.duration || 1),
        selected: !!holiday.selected,
    })), [calendarSettings]);

    const customHolidays = useMemo(() => (calendarSettings?.custom || []).map((holiday) => ({
        date: holiday.date,
        description: holiday.description,
    })), [calendarSettings]);

    // ===================== JOURS DE TRAVAIL =====================
    const [workDays] = useState({
        lundi: { isWorking: true, hours: 8 },
        mardi: { isWorking: true, hours: 8 },
        mercredi: { isWorking: true, hours: 8 },
        jeudi: { isWorking: true, hours: 8 },
        vendredi: { isWorking: true, hours: 8 },
        samedi: { isWorking: false, hours: 0 },
        dimanche: { isWorking: false, hours: 0 },
    });

    const WEEKDAYS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
    const WEEKDAYS_SHORT = ["L", "M", "M", "J", "V", "S", "D"];

    // ===================== ✅ SOLDES TRANSFORMÉS ET TRIÉS =====================
    const soldesConges = useMemo(() => {
        const transformed = (userSoldes || []).map((solde) => {
            const soldeInitial = Number(solde.soldeInitial || 0);
            const soldeAcquis = Number(solde.soldeAcquis || 0);
            const soldeConsomme = Number(solde.soldeConsomme || 0);
            const soldeRestant = Number(solde.soldeRestant || 0);

            return {
                id: solde.id,
                absenceTypeId: solde.absenceTypeId,
                type: solde.absenceTypeNom || solde.absenceTypeName || "Type inconnu",
                code: solde.absenceTypeCode || "",
                couleur: solde.absenceTypeCouleur || "#3b82f6",
                soldeTotal: soldeInitial + soldeAcquis,
                initial: soldeInitial,
                acquis: soldeAcquis,
                pris: soldeConsomme,
                enAttente: 0,
                restant: soldeRestant,
                periodeReference: `${solde.annee || new Date().getFullYear()}`,
                reportPossible: 0,
                dateExpiration: solde.dateExpiration || null,
            };
        });

        return transformed.sort((a, b) => b.restant - a.restant);
    }, [userSoldes]);

    const findMatchingSolde = (absenceTypeId, typeLabel) => {
        if (!soldesConges || soldesConges.length === 0) return null;
        if (absenceTypeId) {
            const foundById = soldesConges.find((s) => String(s.absenceTypeId) === String(absenceTypeId));
            if (foundById) return foundById;
        }
        if (typeLabel) {
            const normTarget = String(typeLabel).toLowerCase().trim();
            const foundByName = soldesConges.find((s) => String(s.type).toLowerCase().trim() === normTarget);
            if (foundByName) return foundByName;
        }
        return null;
    };

    const soldePrincipal = useMemo(() => {
        if (!soldesConges || soldesConges.length === 0) return null;
        const annualLeave = soldesConges.find(
            (s) => s.type && s.type.toLowerCase().includes("annuel")
        );
        return annualLeave || soldesConges[0];
    }, [soldesConges]);

    // ===================== FORMULAIRE =====================
    const [formData, setFormData] = useState({
        absenceTypeId: "",
        nature: "CONGE",
        heureSortie: "",
        dateDebut: "",
        dateDebutType: "MATIN",
        dateFin: "",
        dateFinType: "APRES_MIDI",
        dateReprise: "",
        commentaire: "",
        justificatif: null,
        autorisation: null,
        demiJourneeDebut: false,
        demiJourneeDebutType: "MATIN",
        demiJourneeFin: false,
        demiJourneeFinType: "MATIN",
        remplacantId: "",
        remplacantServiceId: "",
    });
    const [remplacantQuery, setRemplacantQuery] = useState("");
    const [showRemplacants, setShowRemplacants] = useState(false);

    const [justificatifUpload, setJustificatifUpload] = useState({
        demandeId: null,
        fichier: null,
    });

    useEffect(() => {
        const isValidUser = userId && userId !== "0" && userId !== 0;
        const isValidCompany = companyId && companyId !== "0" && companyId !== 0;

        if (isValidUser && isValidCompany) {
            if (activeTab === "solde" && selectedHistoryDate) {
                dispatch(getUserSoldesRequest({ userId, companyId, date: selectedHistoryDate }));
            } else if (activeTab === "demande" && formData.dateDebut) {
                dispatch(getUserSoldesRequest({ userId, companyId, date: formData.dateDebut }));
            } else {
                dispatch(getUserSoldesRequest({ userId, companyId }));
            }
        }
    }, [activeTab, selectedHistoryDate, formData.dateDebut, userId, companyId, dispatch]);

    // ===================== INIT LOAD =====================
    useEffect(() => {
        dispatch(getMyLeaveRequestsRequest());
        dispatch(getAllUsersRequest());
        dispatch(getAllEntitesRequest());

        if (!typesAbsences || typesAbsences.length === 0) {
            dispatch(fetchParamCongesRequest());
        }
        dispatch(getWorkdaysConfigRequest());

        const isValidUser = userId && userId !== "0" && userId !== 0;
        const isValidCompany = companyId && companyId !== "0" && companyId !== 0;

        if (isValidUser && isValidCompany) {
            dispatch(getUserSoldesRequest({ userId, companyId }));
        }

        if (isValidUser) {
            // Utilise la route utilisateur (qui renvoie maintenant tout le contenu via le backend)
            dispatch(getUserMovementsRequest(userId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const entitesParId = useMemo(() => new Map(
        (entites || []).map((entite) => [String(entite.id), entite])
    ), [entites]);

    // Un service est une entité de niveau 2 : elle a un parent, mais ce parent est à la racine.
    const estService = (entite) => {
        if (!entite?.parentEntityId) return false;
        const parent = entitesParId.get(String(entite.parentEntityId));
        return !!parent && !parent.parentEntityId;
    };

    const getServiceDuCollaborateur = (entityId) => {
        let entite = entitesParId.get(String(entityId));
        const visites = new Set();
        while (entite && !visites.has(String(entite.id))) {
            if (estService(entite)) return entite;
            visites.add(String(entite.id));
            entite = entite.parentEntityId ? entitesParId.get(String(entite.parentEntityId)) : null;
        }
        return null;
    };

    const servicesRemplacant = useMemo(() => (
        (entites || [])
            .filter((entite) => entite.isActive !== false && estService(entite))
            .sort((a, b) => a.name.localeCompare(b.name))
    ), [entites, entitesParId]);

    const remplacantsDisponibles = useMemo(() => (
        (companyUsers || [])
            .filter((user) => String(user.id) !== String(userId))
            .filter((user) => user.isActive !== false)
            .filter((user) => !!getServiceDuCollaborateur(user.entityId))
            .filter((user) => !formData.remplacantServiceId || String(getServiceDuCollaborateur(user.entityId)?.id) === String(formData.remplacantServiceId))
            .sort((a, b) => `${a.lastName || ""} ${a.firstName || ""}`.localeCompare(`${b.lastName || ""} ${b.firstName || ""}`))
    ), [companyUsers, formData.remplacantServiceId, userId, entitesParId]);

    const remplacantsFiltres = useMemo(() => {
        const query = remplacantQuery.trim().toLowerCase();
        if (!query) return remplacantsDisponibles;
        return remplacantsDisponibles.filter((user) =>
            [user.fullName, user.firstName, user.lastName, user.employeeCode, getServiceDuCollaborateur(user.entityId)?.name]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(query))
        );
    }, [remplacantQuery, remplacantsDisponibles, entitesParId]);

    useRequestHistoryAutoRefresh(() => dispatch(getMyLeaveRequestsRequest({ silent: true })));

    useEffect(() => {
        dispatch(getCalendarSettingsRequest(currentMonth.getFullYear(), "MA"));
    }, [currentMonth, dispatch]);

    useEffect(() => {
        if (apiMessage || apiError) dispatch(clearLeaveRequestMessage());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    useEffect(() => {
        if (apiMessage) {
            Swal.fire({
                icon: "success",
                title: t("Opération réussie"),
                text: apiMessage,
                timer: 3000,
                showConfirmButton: false,
            });
            if (apiMessage.includes("succès") || apiMessage.includes("reportée")) {
                const isValidUser = userId && userId !== "0" && userId !== 0;
                const isValidCompany = companyId && companyId !== "0" && companyId !== 0;
                if (isValidUser && isValidCompany) {
                    dispatch(getUserSoldesRequest({ userId, companyId }));
                    dispatch(getMyLeaveRequestsRequest());
                }
            }
            dispatch(clearLeaveRequestMessage());
        }
        if (apiError) {
            Swal.fire({
                icon: "error",
                title: t("Erreur"),
                text: typeof apiError === "string" ? apiError : (apiError.message || t("Une erreur est survenue")),
            });
            dispatch(clearLeaveRequestMessage());
        }
    }, [apiMessage, apiError, dispatch, userId, companyId]);


    const [isDelegationEnabled, setIsDelegationEnabled] = useState(currentUser?.isLeaveDelegationEnabled || false);
    useEffect(() => {
        setIsDelegationEnabled(currentUser?.isLeaveDelegationEnabled || false);
    }, [currentUser]);


    const handleToggleDelegation = async (enabled) => {
        try {
            const token = sessionStorage.getItem("accessToken");
            const tenantId = sessionStorage.getItem("selectedCompanyId");

            const response = await fetch(`${API_BASE_URL}/api/users/${userId}/delegation?enabled=${enabled}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "uid": String(userId),
                    "X-Tenant-Id": tenantId || "",
                    "Authorization": token ? `Bearer ${token}` : ""
                }
            });
            if (response.ok) {
                const updatedUser = await response.json();
                setIsDelegationEnabled(enabled);

                // ✅ Mise à jour du state Redux pour que ça persiste à travers la navigation
                dispatch(setAuthenticatedUser(updatedUser));

                Swal.fire({
                    title: t("Succès"),
                    text: `${t("La délégation est désormais")} ${enabled ? t("activée") : t("désactivée")}.`,
                    icon: "success",
                    timer: 1500,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end'
                });

            } else {
                throw new Error("Erreur lors de la mise à jour");
            }
        } catch (error) {
            console.error("Error toggling delegation:", error);
            Swal.fire("Erreur", "Impossible de mettre à jour la délégation.", "error");
            // Revert switch
            setIsDelegationEnabled(!enabled);
        }
    };


    // ===================== HELPERS =====================
    const safeStr = (v) => (v === null || v === undefined ? "" : String(v));

    const getAbsenceTypeLabel = (t) =>
        t?.label ||
        t?.libelle ||
        t?.name ||
        t?.nom ||
        t?.type ||
        "Type";

    const getAbsenceTypeColor = (t) =>
        t?.couleur ||
        t?.color ||
        t?.hexColor ||
        t?.backgroundColor ||
        "#3b82f6";

    const checkIsHoliday = (d) => {
        if (!d) return false;
        const month = (d.getMonth() + 1).toString().padStart(2, "0");
        const day = d.getDate().toString().padStart(2, "0");
        const dateStr = `${month}-${day}`;
        const isFixed = (fixedHolidays || []).some((h) => h.date === dateStr && h.selected);
        if (isFixed) return true;

        const isHijri = (hijriHolidays || []).some((h) => {
            if (!h.selected || !h.date) return false;
            const holidayDate = new Date(h.date);
            const checkDate = new Date(d);
            holidayDate.setHours(0, 0, 0, 0);
            checkDate.setHours(0, 0, 0, 0);

            for (let i = 0; i < (h.duration || 1); i++) {
                const testDate = new Date(holidayDate);
                testDate.setDate(testDate.getDate() + i);
                testDate.setHours(0, 0, 0, 0);

                if (testDate.getTime() === checkDate.getTime()) return true;
            }
            return false;
        });
        if (isHijri) return true;

        const fullDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        return (customHolidays || []).some((h) => h.date === fullDate);
    };

    const getProchainJourOuvre = (dateStr) => {
        if (!dateStr) return "";
        let d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return "";

        d.setDate(d.getDate() + 1);

        let safetyCount = 0;
        while (safetyCount < 30) {
            const dayIndex = d.getDay();
            const dayName = dayIndex === 0 ? "dimanche" :
                dayIndex === 1 ? "lundi" :
                    dayIndex === 2 ? "mardi" :
                        dayIndex === 3 ? "mercredi" :
                            dayIndex === 4 ? "jeudi" :
                                dayIndex === 5 ? "vendredi" : "samedi";

            let isWorking = true;
            if (workdaysConfig && workdaysConfig.workDays && workdaysConfig.workDays[dayName]) {
                const cfg = workdaysConfig.workDays[dayName];
                isWorking = typeof cfg.isWorking === "boolean" ? cfg.isWorking :
                    typeof cfg.working === "boolean" ? cfg.working : true;
            } else if (workDays && workDays[dayName]) {
                isWorking = workDays[dayName].isWorking;
            }

            if (isWorking && !checkIsHoliday(d)) {
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, "0");
                const dayVal = String(d.getDate()).padStart(2, "0");
                return `${y}-${m}-${dayVal}`;
            }
            d.setDate(d.getDate() + 1);
            safetyCount++;
        }
        return "";
    };

    const calculerDuree = (
        debut = formData.dateDebut,
        fin = formData.dateFin,
        debutType = formData.dateDebutType,
        finType = formData.dateFinType
    ) => {
        if (!debut || !fin) return 0;
        const start = new Date(debut);
        const end = new Date(fin);
        if (start > end) return 0;
        let days = 0;

        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const dayIndex = d.getDay();
            const dayName = dayIndex === 0 ? "dimanche" :
                dayIndex === 1 ? "lundi" :
                    dayIndex === 2 ? "mardi" :
                        dayIndex === 3 ? "mercredi" :
                            dayIndex === 4 ? "jeudi" :
                                dayIndex === 5 ? "vendredi" : "samedi";

            let isWorking = true;
            if (workdaysConfig && workdaysConfig.workDays && workdaysConfig.workDays[dayName]) {
                const cfg = workdaysConfig.workDays[dayName];
                isWorking = typeof cfg.isWorking === "boolean" ? cfg.isWorking :
                    typeof cfg.working === "boolean" ? cfg.working : true;
            } else if (workDays && workDays[dayName]) {
                isWorking = workDays[dayName].isWorking;
            }
            if (isWorking) {
                if (!checkIsHoliday(d)) {
                    const y = d.getFullYear();
                    const month = String(d.getMonth() + 1).padStart(2, "0");
                    const dayVal = String(d.getDate()).padStart(2, "0");
                    const dStr = `${y}-${month}-${dayVal}`;

                    if (debut === fin && dStr === debut && (debutType === "APRES_MIDI" || finType === "MATIN")) {
                        days += 0.5;
                    } else if (dStr === debut && debutType === "APRES_MIDI") {
                        days += 0.5;
                    } else if (dStr === fin && finType === "MATIN") {
                        days += 0.5;
                    } else {
                        days += 1.0;
                    }
                }
            }
        }

        return days;
    };

    const getReprise = (dateFin, dateFinType) => {
        if (!dateFin) return { date: "", type: "MATIN" };
        return dateFinType === "MATIN"
            ? { date: dateFin, type: "APRES_MIDI" }
            : { date: getProchainJourOuvre(dateFin), type: "MATIN" };
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        if (Number.isNaN(date.getTime())) return dateStr;
        return date.toLocaleDateString(i18n.language, { day: "2-digit", month: "short", year: "numeric" });
    };

    const formatMonth = (date) => date.toLocaleDateString(i18n.language, { month: "long", year: "numeric" });

    const translateLeaveType = (label) => {
        const value = String(label || "").trim();
        const normalized = value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[’']/g, " ").replace(/\s+/g, " ").trim();
        const leaveTypeKeys = {
            "conge annuel": "Congé annuel",
            "conge de mariage": "Congé de mariage",
            "conge de naissance": "Congé de naissance",
            "conge de deces": "Congé de décès",
            "conge sans solde": "Congé sans solde",
            "maladie": "Maladie",
            "maladie du demandeur": "Maladie du demandeur",
            "maladie du conjoint": "Maladie du conjoint",
            "maladie d un enfant": "Maladie d’un enfant",
            "maladie d un parent": "Maladie d’un parent",
            "examens medicaux": "Examens médicaux",
            "formation": "Formation",
            "mariage d un enfant": "Mariage d’un enfant",
            "preparation d un dossier administratif (public)": "Préparation d’un dossier administratif (Public)",
            "annual leave": "Congé annuel",
        };
        const translationKey = leaveTypeKeys[normalized];
        if (translationKey) return t(translationKey);
        return value || t("Solde disponible");
    };

    const translateMovementReason = (reason) => {
        const value = String(reason || "");
        const normalized = value.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        if (normalized.startsWith("annulation/rejet demande")) {
            return value.replace(/annulation\/rejet demande/i, t("Annulation/Rejet demande"));
        }
        if (normalized === "acquisition mensuelle") return t("Acquisition mensuelle");
        return value || "—";
    };

    // ===================== TYPES DYNAMIQUES (avec règles) =====================
    const typesCongesDisponibles = useMemo(() => {
        return (typesAbsences || []).filter((t) => t?.actif === true).map((t) => {
            const id = t?.id;
            const label = getAbsenceTypeLabel(t);
            const couleur = getAbsenceTypeColor(t);

            return {
                id,
                value: label,
                label,
                couleur,
                requireJustificatif: !!t?.justificationRequired,
                justificatifMandatory: !!t?.justificationMandatory,
                justificationType: t?.justificationType || "Justificatif",
                requireAutorisation: !!t?.requireAutorisation || !!t?.autorisationObligatoire || !!t?.isAutorisationRequired,
                category: t?.category,
                nbJours: t?.nbJours,
                maxDaysPerRequest: t?.maxDaysPerRequest,
                periodeDe: t?.periodeDe,
                periodeA: t?.periodeA,
                negativeBalanceAllowed: !!t?.negativeBalanceAllowed,
                maxNegativeBalance: t?.maxNegativeBalance,
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typesAbsences]);

    const selectedTypeObj = useMemo(() => {
        if (!formData.absenceTypeId) return null;
        return typesCongesDisponibles.find((t) => String(t.id) === String(formData.absenceTypeId)) || null;
    }, [formData.absenceTypeId, typesCongesDisponibles]);

    const selectedTypeLabel = selectedTypeObj?.label || "";

    // ===================== MAP API -> UI HISTORIQUE =====================
    const historiqueDemandes = useMemo(() => {
        return (myRequests || []).map((d) => {
            const deb = d.dateDebut || d.startDate || d.fromDate;
            const fin = d.dateFin || d.endDate || d.toDate;

            const duree =
                Number.isFinite(d.duree) ? d.duree :
                    (deb && fin ? calculerDuree(deb, fin) : 0);

            const createdAt =
                d.createdAt ||
                d.dateDemande ||
                d.requestDate ||
                d.datesDemande ||
                "";

            const typeNom =
                d.absenceTypeNom ||
                d.absenceTypeName ||
                d.absenceTypeLabel ||
                d.typeConge ||
                "Congé";

            const typeMatch = (typesCongesDisponibles || []).find((t) => t.label === typeNom);

            const statusRaw = safeStr(d.status || d.statut).toUpperCase();
            const statut =
                statusRaw === "PENDING" || statusRaw === "PENDING_MANAGER" || statusRaw === "PENDING_ADMIN" || statusRaw === "PENDING_VALIDATION" || statusRaw === "EN_ATTENTE" ? "enAttente" :
                    statusRaw === "APPROVED" || statusRaw === "APPROUVE" ? "approuve" :
                        statusRaw === "REJECTED" || statusRaw === "REFUSE" ? "refuse" :
                            statusRaw === "CANCELLED" || statusRaw === "ANNULE" ? "annule" :
                                statusRaw === "POSTPONED" || statusRaw === "REPORTE" ? "reporte" :
                                    statusRaw === "POSTPONEMENT_REQUEST" || statusRaw === "POSTPONED_REQUEST" ? "reporte_en_attente" :
                                        (d.status || d.statut || "enAttente");


            const motifRejet = d.motifRejet || d.rejectionReason || d.managerComment || d.adminComment || "";

            return {
                id: d.id,
                typeConge: typeNom,
                couleurType: typeMatch?.couleur || "#3b82f6",
                dateDebut: deb,
                dateFin: fin,
                duree,
                datesDemande: createdAt ? String(createdAt).slice(0, 10) : "",
                commentaire: d.commentaire || d.comment || "",
                statut,
                rawStatus: statusRaw,
                managerName: d.managerName || null,
                managerDecision: d.managerDecision ?? null,
                managerDecisionAt: d.managerDecisionAt || null,
                managerComment: d.managerComment || null,
                adminName: d.adminName || null,
                adminDecision: d.adminDecision ?? null,
                adminDecisionAt: d.adminDecisionAt || null,
                adminComment: d.adminComment || null,
                justificatifFourni: !!d.justificatifFourni,
                justificatifNom: d.justificatifNom || d.justificatifName || null,
                autorisationFournie: !!d.autorisationFournie,
                autorisationNom: d.autorisationNom || d.autorisationName || null,
                motifRejet,
                soldeInitial: d.soldeInitial,
                soldeConsomme: d.soldeConsomme,
                soldeRestant: d.soldeRestant,
                totalGlobal: d.totalRestant,
                originalDateDebut: d.originalDateDebut,
                originalDateFin: d.originalDateFin,
                dateReprise: d.dateReprise || null,
                employeeName: d.employeeName || "",
                employeeCode: d.employeeCode || "",
                demandeurName: d.demandeurName || "",
                demandeurCode: d.demandeurCode || "",
                replacement: d.replacement || null,
                hasReplacementHistory: !!d.hasReplacementHistory,
                previousRequestId: d.previousRequestId || null,
            };


        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myRequests, typesCongesDisponibles]);

    const historyRows = useMemo(() => {
        const byId = new Map(historiqueDemandes.map((item) => [item.id, item]));
        const childrenByParent = new Map();
        historiqueDemandes.forEach((item) => {
            if (item.previousRequestId && byId.has(item.previousRequestId)) {
                childrenByParent.set(item.previousRequestId, [...(childrenByParent.get(item.previousRequestId) || []), item]);
            }
        });
        return historiqueDemandes.reduce((rows, item) => {
            if (item.previousRequestId && byId.has(item.previousRequestId)) return rows;
            const children = childrenByParent.get(item.id) || [];
            rows.push({ folder: children.length > 0, key: item.id, requests: [item, ...children] });
            return rows;
        }, []);
    }, [historiqueDemandes]);

    const toggleLeaveFolder = (id) => setClosedLeaveFolders((current) => {
        const next = new Set(current);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    // ===================== ✅ APPROVED REQUESTS & TOTAL BALANCE =====================
    const totalRestant = useMemo(() => {
        return soldesConges.reduce((acc, s) => acc + (Number(s.restant) || 0), 0);
    }, [soldesConges]);

    const approvedRequests = useMemo(() => {
        return historiqueDemandes.filter((d) => d.statut === "approuve" || d.statut === "annule" || d.statut === "reporte");
    }, [historiqueDemandes]);

    const combinedHistory = useMemo(() => {
        // 1. Filtrer les demandes : uniquement approuvées, annulées, reportées
        const filteredRequests = (historiqueDemandes || []).filter((req) =>
            req.statut === "approuve" || req.statut === "annule" || req.statut === "reporte"
        );
        // Convertir en structure de mouvements (débits)
        const requestsAsMovements = filteredRequests.map((req) => {
            const dateStr = `${t("Du")} ${formatDate(req.dateDebut)} ${t("au")} ${formatDate(req.dateFin)}`;
            const motifText = req.commentaire ? `${dateStr} - ${req.commentaire}` : dateStr;

            // Pour les demandes, la valeur de débit est négative
            const valeur = -Number(req.duree || 0);

            const refText = req.demandeurName
                ? `${req.demandeurName} (${req.demandeurCode || "—"})`
                : `REF-${req.id}`;

            return {
                id: `req-${req.id}`,
                employeeName: req.employeeName,
                employeeCode: req.employeeCode,
                dateMouvement: req.createdAt || req.dateDebut,
                reference: refText,
                motif: motifText,
                commentaire: req.motifRejet ? `${t("Motif rejet/annulation")} : ${req.motifRejet}` : "",
                absenceTypeNom: req.typeConge,
                absenceTypeCouleur: req.couleurType,
                soldeInitialSnapshot: req.soldeInitial,
                soldeRestantSnapshot: req.soldeRestant,
                valeur: valeur,
                isLeaveRequest: true,
                statut: req.statut,
            };
        });

        // 2. Filtrer les mouvements pour ne garder que ceux non liés à une demande (exclure CONSUMPTION)
        const positiveMovements = (movements || [])
            .filter((m) => m.typeMouvement !== "CONSUMPTION")
            .map((m) => ({
                id: `mov-${m.id}`,
                employeeName: m.employeeName,
                employeeCode: m.employeeCode,
                dateMouvement: m.dateMouvement,
                reference: m.crediteurName ? `${m.crediteurName} (${m.crediteurMatricule || "—"})` : "SYS_AUTO",
                motif: m.commentaire || m.motif || "Ajustement",
                commentaire: "",
                absenceTypeNom: m.absenceTypeNom,
                absenceTypeCouleur: m.absenceTypeCouleur,
                soldeInitialSnapshot: m.soldeInitialSnapshot,
                soldeRestantSnapshot: m.soldeRestantSnapshot,
                valeur: Number(m.valeur || 0),
                isLeaveRequest: false,
                statut: "valide",
            }));

        // 3. Fusionner les deux listes
        const merged = [...requestsAsMovements, ...positiveMovements];

        // 4. Trier par date décroissante
        return merged.sort((a, b) => {
            const dateA = a.dateMouvement ? new Date(a.dateMouvement).getTime() : 0;
            const dateB = b.dateMouvement ? new Date(b.dateMouvement).getTime() : 0;
            return dateB - dateA;
        });
    }, [historiqueDemandes, movements, i18n.language]);

    // ===================== ✅ CALENDAR HELPERS AMÉLIORÉS =====================

    // Férié fixe (MM-DD)
    const isFixedHoliday = (date) => {
        if (!date) return false;
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        const dateStr = `${month}-${day}`;
        return fixedHolidays.some((h) => h.date === dateStr && h.selected);
    };

    // ✅ Férié Hijri (multi-jours)
    const isHijriHoliday = (date) => {
        if (!date) return false;

        return hijriHolidays.some((h) => {
            if (!h.selected || !h.date) return false;

            const holidayDate = new Date(h.date);
            const checkDate = new Date(date);
            holidayDate.setHours(0, 0, 0, 0);
            checkDate.setHours(0, 0, 0, 0);

            for (let i = 0; i < (h.duration || 1); i++) {
                const testDate = new Date(holidayDate);
                testDate.setDate(testDate.getDate() + i);
                testDate.setHours(0, 0, 0, 0);

                if (testDate.getTime() === checkDate.getTime()) return true;
            }
            return false;
        });
    };

    const isCustomHoliday = (date) => {
        if (!date) return false;
        const fullDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
        return customHolidays.some((holiday) => holiday.date === fullDate);
    };

    const isNonWorkingDay = (date) => {
        if (!date) return false;
        let dayIndex = date.getDay();
        dayIndex = dayIndex === 0 ? 6 : dayIndex - 1;
        const dayName = WEEKDAYS[dayIndex];
        const configuredDay = workdaysConfig?.workDays?.[dayName] || workDays[dayName];
        const isWorking = typeof configuredDay?.isWorking === "boolean"
            ? configuredDay.isWorking
            : typeof configuredDay?.working === "boolean"
                ? configuredDay.working
                : !(dayName === "samedi" || dayName === "dimanche");
        return !isWorking;
    };

    const getCongeInfo = (date) => {
        if (!date) return null;
        const y = date.getFullYear();
        const m = (date.getMonth() + 1).toString().padStart(2, "0");
        const d = date.getDate().toString().padStart(2, "0");
        const dateStr = `${y}-${m}-${d}`;

        return historiqueDemandes.find((demande) => {
            if (demande.statut === "refuse" || demande.statut === "annule") return false;
            const debutDate = new Date(demande.dateDebut);
            const finDate = new Date(demande.dateFin);
            const checkDate = new Date(dateStr);
            debutDate.setHours(0, 0, 0, 0);
            finDate.setHours(0, 0, 0, 0);
            checkDate.setHours(0, 0, 0, 0);
            return checkDate >= debutDate && checkDate <= finDate;
        });
    };

    const getPublicHolidayName = (date) => {
        if (!date) return "";
        const names = [];
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const fixedHoliday = fixedHolidays.find((holiday) => holiday.date === `${month}-${day}` && holiday.selected);
        if (fixedHoliday) names.push(fixedHoliday.description);

        const fullDate = `${date.getFullYear()}-${month}-${day}`;
        hijriHolidays.forEach((holiday) => {
            if (!holiday.selected || !holiday.date) return;
            const start = new Date(holiday.date);
            start.setHours(0, 0, 0, 0);
            for (let i = 0; i < (holiday.duration || 1); i++) {
                const occurrence = new Date(start);
                occurrence.setDate(occurrence.getDate() + i);
                const occurrenceDate = `${occurrence.getFullYear()}-${String(occurrence.getMonth() + 1).padStart(2, "0")}-${String(occurrence.getDate()).padStart(2, "0")}`;
                if (occurrenceDate === fullDate) {
                    names.push(holiday.duration > 1 ? `${holiday.name} (${i + 1}/${holiday.duration})` : holiday.name);
                    break;
                }
            }
        });

        const customHoliday = customHolidays.find((holiday) => holiday.date === fullDate);
        if (customHoliday) names.push(customHoliday.description);
        return names.join(" • ");
    };

    // ✅ Nom du jour avec TOUS les types de fériés
    const getHolidayName = (date) => {
        if (!date) return "";
        const names = [];

        // Férié fixe
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        const dateStr = `${month}-${day}`;
        const fixedHoliday = fixedHolidays.find((h) => h.date === dateStr && h.selected);
        if (fixedHoliday) names.push(`🎉 ${fixedHoliday.description}`);

        // ✅ Férié Hijri (avec indicateur de jour)
        const year = date.getFullYear();
        const m2 = (date.getMonth() + 1).toString().padStart(2, "0");
        const d2 = date.getDate().toString().padStart(2, "0");
        const fullDate = `${year}-${m2}-${d2}`;

        hijriHolidays.forEach((h) => {
            if (!h.selected || !h.date) return;

            const holidayDate = new Date(h.date);
            holidayDate.setHours(0, 0, 0, 0);

            for (let i = 0; i < (h.duration || 1); i++) {
                const test = new Date(holidayDate);
                test.setDate(test.getDate() + i);
                test.setHours(0, 0, 0, 0);

                const testStr = `${test.getFullYear()}-${(test.getMonth() + 1).toString().padStart(2, "0")}-${test.getDate().toString().padStart(2, "0")}`;

                if (testStr === fullDate) {
                    const dayLabel = h.duration > 1 ? ` (Jour ${i + 1}/${h.duration})` : "";
                    names.push(`🌙 ${h.name}${dayLabel}`);
                    break;
                }
            }
        });

        const customHoliday = customHolidays.find((h) => h.date === fullDate);
        if (customHoliday) names.push(`🎉 ${customHoliday.description}`);

        // Congé personnel
        const congeInfo = getCongeInfo(date);
        if (congeInfo) {
            let statutLabel = " (Approuvé)";
            if (congeInfo.statut === "enAttente") statutLabel = " (En attente)";
            if (congeInfo.statut === "reporte") statutLabel = " (Reporté)";
            names.push(`📅 ${congeInfo.typeConge}${statutLabel}`);
        }

        return names.join(" • ");
    };

    const getMonthData = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        let startOffset = firstDay.getDay() - 1;
        if (startOffset === -1) startOffset = 6;

        const daysInMonth = lastDay.getDate();
        const weeks = [];
        let currentWeek = [];

        for (let i = 0; i < startOffset; i++) currentWeek.push(null);

        for (let day = 1; day <= daysInMonth; day++) {
            if (currentWeek.length === 7) {
                weeks.push(currentWeek);
                currentWeek = [];
            }
            currentWeek.push(new Date(year, month, day));
        }

        while (currentWeek.length < 7) currentWeek.push(null);
        weeks.push(currentWeek);
        return weeks;
    };

    // ✅ Classes CSS améliorées (priorités: congé > hijri > fixe > weekend)
    const getDayClass = (date) => {
        if (!date) return "dcDay--empty";

        // Priorité 1: Congés personnels
        const congeInfo = getCongeInfo(date);
        if (congeInfo) {
            if (congeInfo.statut === "enAttente") return "dcDay--pending";
            if (congeInfo.statut === "approuve" || congeInfo.statut === "reporte") return "dcDay--approved";
        }

        // Priorité 2: Fériés Hijri
        if (isHijriHoliday(date)) return "dcDay--hijri";

        // Priorité 3: Fériés fixes et personnalisés
        if (isFixedHoliday(date)) return "dcDay--holiday";
        if (isCustomHoliday(date)) return "dcDay--custom";

        // Priorité 4: Weekend
        if (isNonWorkingDay(date)) return "dcDay--weekend";

        return "dcDay--normal";
    };

    // ===================== BADGE =====================
    const getStatutBadge = (statut) => {
        const config = {
            enAttente: { label: t("En attente"), bg: "#fff7db", color: "#8a6a00", icon: Clock },
            approuve: { label: t("Approuvé"), bg: "#e9f8ef", color: "#0f6b3f", icon: CheckCircle },
            refuse: { label: t("Refusé"), bg: "#ffe7ea", color: "#8b1d2b", icon: XCircle },
            annule: { label: t("Annulé"), bg: "#f3f4f6", color: "#6b7280", icon: XCircle },
            reporte: { label: t("Reporté"), bg: "#e0f2fe", color: "#0369a1", icon: RefreshCw },
            reporte_en_attente: { label: t("En attente de report"), bg: "#f0f9ff", color: "#0c4a6e", icon: RefreshCw },
        };

        const { label, bg, color, icon: Icon } = config[statut] || config.enAttente;
        return (
            <span className="dcBadge" style={{ backgroundColor: bg, color }}>
                <Icon size={14} />
                {label}
            </span>
        );
    };

    // ===================== COUNTS =====================
    const pendingCount = historiqueDemandes.filter((d) => d.statut === "enAttente" || d.statut === "reporte_en_attente").length;

    const approvedCount = historiqueDemandes.filter((d) => d.statut === "approuve").length;
    const reportedCount = historiqueDemandes.filter((d) => d.statut === "reporte").length;
    const refusedCount = historiqueDemandes.filter((d) => d.statut === "refuse").length;
    const cancelledCount = historiqueDemandes.filter((d) => d.statut === "annule").length;


    // ===================== SUBMIT (API) =====================
    const handleSubmitDemande = (e) => {
        e.preventDefault();
        setSoldeError("");

        if (!formData.absenceTypeId) {
            setSoldeError(t("Veuillez sélectionner un type de congé."));
            return;
        }
        if (!formData.dateDebut || !formData.dateFin) {
            setSoldeError(t("Veuillez renseigner la date de début et la date de fin."));
            return;
        }
        if (!formData.dateReprise) {
            setSoldeError(t("Veuillez renseigner la date de reprise."));
            return;
        }
        if (new Date(formData.dateReprise) < new Date(formData.dateFin)) {
            setSoldeError(t("La date de reprise ne peut pas être antérieure à la date de fin."));
            return;
        }
        if (formData.dateDebut === formData.dateFin
            && formData.dateDebutType === "APRES_MIDI"
            && formData.dateFinType === "MATIN") {
            setSoldeError(t("La fin le matin ne peut pas précéder un début l'après-midi le même jour."));
            return;
        }

        const selectedTypeLabel = getAbsenceTypeLabel(typesAbsences.find(t => String(t.id) === String(formData.absenceTypeId)));
        const duree = calculerDuree(formData.dateDebut, formData.dateFin);

        // ✅ VÉRIFICATION DU MAX DE JOURS PAR PRISE
        if (selectedTypeObj?.maxDaysPerRequest && duree > selectedTypeObj.maxDaysPerRequest) {
            setSoldeError(
                `La durée choisie (${duree} jour${duree > 1 ? "s" : ""}) dépasse le maximum autorisé pour ce type de congé (${selectedTypeObj.maxDaysPerRequest} jour${selectedTypeObj.maxDaysPerRequest > 1 ? "s" : ""}).`
            );
            return;
        }

        // ✅ VÉRIFICATION PÉRIODE DE RÉFÉRENCE
        if (selectedTypeObj?.periodeDe && formData.dateDebut && new Date(formData.dateDebut) < new Date(selectedTypeObj.periodeDe)) {
            setSoldeError(`${t("Ce type de congé ne peut pas être pris avant le")} ${formatDate(selectedTypeObj.periodeDe)} (${t("période de référence")}).`);
            return;
        }
        if (selectedTypeObj?.periodeA && formData.dateFin && new Date(formData.dateFin) > new Date(selectedTypeObj.periodeA)) {
            setSoldeError(`${t("Ce type de congé ne peut pas être pris après le")} ${formatDate(selectedTypeObj.periodeA)} (${t("période de référence")}).`);
            return;
        }

        // ✅ JUSTIFICATIF & AUTORISATION
        if (selectedTypeObj?.requireJustificatif && selectedTypeObj?.justificatifMandatory && !formData.justificatif) {
            setSoldeError(`${t("Le document")} "${selectedTypeObj.justificationType}" ${t("est obligatoire pour ce type de congé.")}`);
            return;
        }
        if (selectedTypeObj?.requireAutorisation && !formData.autorisation) {
            setSoldeError(t("L'autorisation est obligatoire pour ce type de congé."));
            return;
        }

        const payload = {
            absenceTypeId: Number(formData.absenceTypeId),
            nature: formData.nature,
            heureSortie: formData.heureSortie || null,
            dateDebut: formData.dateDebut,
            dateFin: formData.dateFin,
            dateReprise: formData.dateReprise || null,
            commentaire: formData.commentaire || "",
            requestType: "conge",
            justificatif: formData.justificatif,
            autorisation: formData.autorisation,
            demiJourneeDebut: formData.dateDebutType === "APRES_MIDI",
            demiJourneeDebutType: formData.dateDebutType,
            demiJourneeFin: formData.dateFinType === "MATIN",
            demiJourneeFinType: formData.dateFinType,
            remplacantId: formData.remplacantId ? Number(formData.remplacantId) : null,
        };

        const executeSubmission = (excessDaysDeductionSource = null) => {
            dispatch(createLeaveRequestRequest({ ...payload, excessDaysDeductionSource }));
            setFormData({
                absenceTypeId: "",
                nature: "CONGE",
                heureSortie: "",
                dateDebut: "",
                dateDebutType: "MATIN",
                dateFin: "",
                dateFinType: "APRES_MIDI",
                dateReprise: "",
                commentaire: "",
                justificatif: null,
                autorisation: null,
                demiJourneeDebut: false,
                demiJourneeDebutType: "MATIN",
                demiJourneeFin: false,
                demiJourneeFinType: "MATIN",
                remplacantId: "",
                remplacantServiceId: "",
            });
            setRemplacantQuery("");
        };

        if (selectedTypeObj?.category === "SPECIAL"
            && selectedTypeObj.nbJours !== null
            && selectedTypeObj.nbJours !== undefined
            && duree > Number(selectedTypeObj.nbJours)) {
            const excessDays = duree - Number(selectedTypeObj.nbJours);
            Swal.fire({
                title: "Jours autorisés dépassés",
                html: `
                    <div class="dcExcessDays">
                        <div class="dcExcessDays__summary">
                            <span>Autorisés <strong>${selectedTypeObj.nbJours} j</strong></span>
                            <span>Demandés <strong>${duree} j</strong></span>
                            <span class="dcExcessDays__excess">À traiter <strong>${excessDays} j</strong></span>
                        </div>
                        <p class="dcExcessDays__prompt">Choisissez l'origine de la déduction pour les jours dépassant la limite :</p>
                        <label class="dcExcessDays__choice is-selected">
                            <input type="radio" name="excess-days-source" value="SALARY" checked />
                            <span class="dcExcessDays__radio"></span>
                            <span><strong>Déduire du salaire</strong><small>Les ${excessDays} jour${excessDays > 1 ? "s" : ""} seront signalés pour retenue sur salaire.</small></span>
                        </label>
                        <label class="dcExcessDays__choice">
                            <input type="radio" name="excess-days-source" value="LEAVE_BALANCE" />
                            <span class="dcExcessDays__radio"></span>
                            <span><strong>Déduire du solde de congé</strong><small>Les ${excessDays} jour${excessDays > 1 ? "s" : ""} seront signalés pour déduction du solde.</small></span>
                        </label>
                    </div>
                `,
                showCancelButton: true,
                confirmButtonText: "Confirmer la demande",
                cancelButtonText: "Annuler",
                confirmButtonColor: "#204385",
                cancelButtonColor: "#64748b",
                customClass: { popup: "dcExcessDaysPopup", confirmButton: "dcExcessDaysConfirm" },
                didOpen: () => {
                    const choices = Swal.getPopup().querySelectorAll(".dcExcessDays__choice");
                    choices.forEach((choice) => choice.addEventListener("click", () => {
                        choices.forEach((item) => item.classList.remove("is-selected"));
                        choice.classList.add("is-selected");
                        choice.querySelector("input").checked = true;
                    }));
                },
                preConfirm: () => Swal.getPopup().querySelector('input[name="excess-days-source"]:checked')?.value,
            }).then((result) => {
                if (result.isConfirmed && result.value) {
                    executeSubmission(result.value);
                }
            });
            return;
        }

        // ✅ VÉRIFICATION DU SOLDE (AVEC OPTION NÉGATIF)
        const typeCongeObj = findMatchingSolde(formData.absenceTypeId, selectedTypeLabel);
        if (typeCongeObj && typeCongeObj.restant < duree) {
            if (selectedTypeObj?.negativeBalanceAllowed) {
                const futureBalance = typeCongeObj.restant - duree;

                // Si un seuil maximum est défini
                if (selectedTypeObj.maxNegativeBalance !== null &&
                    selectedTypeObj.maxNegativeBalance !== undefined &&
                    selectedTypeObj.maxNegativeBalance > 0 &&
                    Math.abs(futureBalance) > selectedTypeObj.maxNegativeBalance) {
                    setSoldeError(
                        `Le solde négatif maximum autorisé (${selectedTypeObj.maxNegativeBalance} jours) sera dépassé. Votre solde actuel est de ${typeCongeObj.restant} jours.`
                    );
                    return;
                }

                // Le solde négatif est autorisé : soumettre directement la demande.
                executeSubmission();
                return;
            } else {
                // ❌ Solde négatif NON autorisé → bloquer
                setSoldeError(
                    `La période choisie (${duree} jour${duree > 1 ? "s" : ""}) dépasse votre solde disponible de ${typeCongeObj.type} (${typeCongeObj.restant} jour${typeCongeObj.restant > 1 ? "s" : ""}).`
                );
                return;
            }
        }

        executeSubmission();
    };

    // ===================== JUSTIFICATIF (UI ONLY) =====================
    const handleUploadJustificatif = (e) => {
        e.preventDefault();
        setShowJustificatifModal(false);
        setJustificatifUpload({ demandeId: null, fichier: null });
    };

    // ===================== ANNULER (API) =====================
    const handleAnnulerDemande = () => {
        if (!selectedDemande) return;

        dispatch(cancelLeaveRequestRequest(selectedDemande.id));

        setShowAnnulationModal(false);
        setSelectedDemande(null);
    };

    // Aligne les actions proposées avec les règles API : une demande approuvée
    // ne peut être annulée que tant que sa date de début n'est pas passée.
    const canCancelLeaveRequest = (demande) => {
        const status = String(demande?.rawStatus || "").toUpperCase();
        if (["PENDING", "PENDING_MANAGER", "PENDING_ADMIN", "PENDING_VALIDATION"].includes(status)) {
            return true;
        }
        if (status !== "APPROVED" || !demande?.dateDebut) return false;

        const startDate = new Date(`${String(demande.dateDebut).slice(0, 10)}T00:00:00`);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return !Number.isNaN(startDate.getTime()) && startDate >= today;
    };

    const loadReplacementTasks = async () => {
        if (!userId) return;
        setReplacementTasksLoading(true);
        try {
            const token = sessionStorage.getItem("token") || sessionStorage.getItem("accessToken");
            const response = await fetch(`${API_BASE_URL}/api/leave-requests/replacements/mine`, {
                headers: { Authorization: token ? `Bearer ${token}` : "", uid: String(userId) },
            });
            if (!response.ok) throw new Error("Impossible de charger les demandes de remplacement.");
            setReplacementTasks(await response.json());
        } catch (err) {
            setSoldeError(err.message);
        } finally {
            setReplacementTasksLoading(false);
        }
    };

    const answerReplacement = async (replacementId, accepted) => {
        let comment = "";
        if (!accepted) {
            const result = await Swal.fire({
                title: "Refuser l'intérim",
                text: "Votre motif sera communiqué au collaborateur, au manager et à RH.",
                input: "textarea",
                inputLabel: "Motif du refus",
                inputPlaceholder: "Expliquez pourquoi vous n'êtes pas disponible...",
                inputValidator: (value) => !String(value || "").trim() && "Le motif est obligatoire.",
                showCancelButton: true,
                confirmButtonText: "Confirmer le refus",
                cancelButtonText: "Annuler",
                confirmButtonColor: "#dc2626",
            });
            if (!result.isConfirmed) return;
            comment = result.value.trim();
        }
        const token = sessionStorage.getItem("token") || sessionStorage.getItem("accessToken");
        const response = await fetch(`${API_BASE_URL}/api/leave-requests/replacements/${replacementId}/decision`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: token ? `Bearer ${token}` : "", uid: String(userId) },
            body: JSON.stringify({ accepted, comment }),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.message || "Impossible d'enregistrer votre réponse.");
        await loadReplacementTasks();
        Swal.fire({ icon: "success", text: accepted ? "Disponibilité confirmée." : "Remplacement refusé.", timer: 1600, showConfirmButton: false });
    };

    const showReplacementHistory = async (requestId) => {
        const token = sessionStorage.getItem("token") || sessionStorage.getItem("accessToken");
        const response = await fetch(`${API_BASE_URL}/api/leave-requests/requests/${requestId}/replacement-history`, {
            headers: { Authorization: token ? `Bearer ${token}` : "", uid: String(userId) },
        });
        const body = await response.json().catch(() => []);
        if (!response.ok) return Swal.fire({ icon: "error", text: body.message || "Historique indisponible." });
        setReplacementHistory(body);
        setReplacementHistoryFor(requestId);
    };

    useEffect(() => { loadReplacementTasks(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

    // ===================== TABS =====================
    const tabs = [
        { id: "solde", label: "Mon solde de congés", icon: Calendar },
        { id: "historique", label: "Mes demandes", icon: FileText },
        { id: "agenda", label: "Agenda", icon: Calendar },
        { id: "demande", label: "Nouvelle demande", icon: Plus },
        { id: "remplacements", label: "Mes intérims", icon: RefreshCw },
    ];

    return (
        <div className="dcPage">
            <div className="dcWrap">
                {/* Header */}
                <div className="dcHeader dcMax">
                    <h1>{t("Mes congés et absences")}</h1>
                    <p>{t("Soumettez une nouvelle demande et suivez son avancement.")}</p>
                </div>

                <div className={`dcCard dcMax${activeTab === "demande" ? " dcCard--overflowVisible" : ""}`}>
                    {/* Tabs */}
                    <div className="dcTabs">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`dcTab ${activeTab === tab.id ? "is-active" : ""}`}
                                    type="button"
                                >
                                    <Icon size={18} />
                                    {t(tab.label)}
                                </button>
                            );
                        })}
                    </div>

                    <div className="dcBody">
                        {/* API error */}
                        {apiError && (
                            <div className="dcAlert" style={{ marginBottom: 14 }}>
                                <AlertCircle size={18} color="#9f1239" style={{ marginTop: 1 }} />
                                <div>
                                    <h4>{t("Erreur")}</h4>
                                    <p>{apiError}</p>
                                </div>
                            </div>
                        )}

                        {/* ===== SOLDE ===== */}
                        {activeTab === "solde" && (
                            <div>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
                                    <div>
                                        <h2 className="dcH2">{t("Mon solde de congés")}</h2>
                                        <p className="dcSub">
                                            {selectedHistoryDate
                                                ? `${t("Solde de vos congés à la date du")} ${formatDate(selectedHistoryDate)}`
                                                : t("Vue d'ensemble de vos congés disponibles")}
                                        </p>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <label style={{ fontSize: "13px", fontWeight: "600", color: "#374151", display: "flex", alignItems: "center", gap: "6px" }}>
                                            <Calendar size={15} color="#204385" />
                                            {t("Solde à la date du :")}
                                        </label>
                                        <input
                                            type="date"
                                            className="dcInput"
                                            style={{ width: "auto", padding: "6px 10px", fontSize: "13px" }}
                                            value={selectedHistoryDate}
                                            onChange={(e) => setSelectedHistoryDate(e.target.value)}
                                        />
                                        {selectedHistoryDate && (
                                            <button
                                                type="button"
                                                className="dcBtn dcBtn--ghost"
                                                style={{ padding: "6px 10px", fontSize: "12px" }}
                                                onClick={() => setSelectedHistoryDate("")}
                                            >
                                                {t("Aujourd'hui")}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="dcKpiGrid">
                                    <div className="dcBigKpi">
                                        <div className="dcBigKpiTop">
                                            <span className="dcBigLabel">
                                                {translateLeaveType(soldePrincipal?.type)}
                                            </span>
                                            <span className="dcBigMeta">{new Date().getFullYear()}</span>
                                        </div>
                                        <div className="dcBigValue">
                                            <span className="dcBigNumber">
                                                {soldePrincipal ? soldePrincipal.restant : 0}
                                            </span>
                                            <span className="dcBigUnit">{t("jours")}</span>
                                        </div>
                                    </div>

                                    <div className="dcKpi" style={{ borderTop: "4px solid #f59e0b", background: "#fffbeb" }}>
                                        <div className="dcKpiTop">
                                            <Clock size={18} color="#f59e0b" />
                                            <span className="dcKpiNum" style={{ color: "#b45309" }}>{pendingCount}</span>
                                        </div>
                                        <div className="dcKpiTitle" style={{ color: "#b45309" }}>{t("En attente")}</div>
                                        <div className="dcKpiSub" style={{ color: "#d97706" }}>
                                            {historiqueDemandes.filter((d) => d.statut === "enAttente").reduce((acc, d) => acc + (d.duree || 0), 0)} {t("jours")}
                                        </div>
                                    </div>

                                    <div className="dcKpi" style={{ borderTop: "4px solid #10b981", background: "#f0fdf4" }}>
                                        <div className="dcKpiTop">
                                            <CheckCircle size={18} color="#10b981" />
                                            <span className="dcKpiNum" style={{ color: "#047857" }}>{approvedCount}</span>
                                        </div>
                                        <div className="dcKpiTitle" style={{ color: "#047857" }}>{t("Approuvées")}</div>
                                        <div className="dcKpiSub" style={{ color: "#059669" }}>
                                            {historiqueDemandes.filter((d) => d.statut === "approuve").reduce((acc, d) => acc + (d.duree || 0), 0)} {t("jours")}
                                        </div>
                                    </div>

                                    <div className="dcKpi" style={{ borderTop: "4px solid #ef4444", background: "#fef2f2" }}>
                                        <div className="dcKpiTop">
                                            <XCircle size={18} color="#ef4444" />
                                            <span className="dcKpiNum" style={{ color: "#b91c1c" }}>{refusedCount}</span>
                                        </div>
                                        <div className="dcKpiTitle" style={{ color: "#b91c1c" }}>{t("Refusées")}</div>
                                        <div className="dcKpiSub" style={{ color: "#dc2626" }}>
                                            {historiqueDemandes.filter((d) => d.statut === "refuse").reduce((acc, d) => acc + (d.duree || 0), 0)} {t("jours")}
                                        </div>
                                    </div>

                                    <div className="dcKpi" style={{ borderTop: "4px solid #6b7280", background: "#f9fafb" }}>
                                        <div className="dcKpiTop">
                                            <XCircle size={18} color="#6b7280" />
                                            <span className="dcKpiNum" style={{ color: "#374151" }}>{cancelledCount}</span>
                                        </div>
                                        <div className="dcKpiTitle" style={{ color: "#374151" }}>{t("Annulées")}</div>
                                        <div className="dcKpiSub" style={{ color: "#4b5563" }}>
                                            {historiqueDemandes.filter((d) => d.statut === "annule").reduce((acc, d) => acc + (d.duree || 0), 0)} {t("jours")}
                                        </div>
                                    </div>

                                    <div className="dcKpi" style={{ borderTop: "4px solid #3b82f6", background: "#eff6ff" }}>
                                        <div className="dcKpiTop">
                                            <RefreshCw size={18} color="#3b82f6" />
                                            <span className="dcKpiNum" style={{ color: "#1d4ed8" }}>{reportedCount}</span>
                                        </div>
                                        <div className="dcKpiTitle" style={{ color: "#1d4ed8" }}>{t("Reportées")}</div>
                                        <div className="dcKpiSub" style={{ color: "#2563eb" }}>
                                            {historiqueDemandes.filter((d) => d.statut === "reporte").reduce((acc, d) => acc + (d.duree || 0), 0)} {t("jours")}
                                        </div>
                                    </div>

                                </div>

                                {/* Délégation Card */}
                                {/*<div className="dcBox" style={{ marginTop: 24, borderLeft: "4px solid #3b82f6", background: "#f0f7ff" }}>*/}
                                {/*    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>*/}
                                {/*        <div>*/}
                                {/*            <h3 className="dcBoxTitle" style={{ marginBottom: 4 }}>*/}
                                {/*                <ShieldCheck size={20} color="#3b82f6" />*/}
                                {/*                Délégation de saisie RH*/}
                                {/*            </h3>*/}
                                {/*            <p style={{ fontSize: "13px", color: "#64748b", margin: 0 }}>*/}
                                {/*                Autoriser l'administration RH à saisir des demandes de congés en votre nom.*/}
                                {/*            </p>*/}
                                {/*        </div>*/}
                                {/*        <div className="dcToggle">*/}
                                {/*            <input */}
                                {/*                type="checkbox" */}
                                {/*                id="delegationToggle" */}
                                {/*                checked={isDelegationEnabled}*/}
                                {/*                onChange={(e) => handleToggleDelegation(e.target.checked)}*/}
                                {/*            />*/}
                                {/*            <label htmlFor="delegationToggle"></label>*/}
                                {/*        </div>*/}
                                {/*    </div>*/}
                                {/*</div>*/}


                                <div className="dcBox" style={{ marginTop: 16 }}>
                                    <h3 className="dcBoxTitle">
                                        <CheckCircle size={18} color="#16a34a" />
                                        {t("États de mes congés")}
                                    </h3>

                                    {(loading || balanceLoading) && combinedHistory.length === 0 ? (
                                        <div className="dcEmpty">
                                            <Clock size={32} />
                                            <div>{t("Chargement...")}</div>
                                        </div>
                                    ) : combinedHistory.length === 0 ? (
                                        <div className="dcEmpty">
                                            <CheckCircle size={32} />
                                            <div>{t("Aucun mouvement trouvé")}</div>
                                        </div>
                                    ) : (
                                        <div className="dcTableWrap">
                                            <table className="dcTable">
                                                <thead>
                                                <tr>
                                                    <th>{t("Référence")}</th>
                                                    <th>{t("Date de l'opération")}</th>
                                                    <th>{t("Motif")}</th>
                                                    <th>{t("Type de congé")}</th>
                                                    <th>{t("Solde Initial")}</th>
                                                    <th>{t("Solde débit")}</th>
                                                    <th>{t("Solde crédit")}</th>
                                                    <th>{t("Solde Restant")}</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {combinedHistory.map((item, index) => {
                                                    const isPositive = item.valeur > 0;
                                                    const isNegative = item.valeur < 0;
                                                    const isCancelledOrRefused = item.isLeaveRequest && (item.statut === "annule" || item.statut === "refuse");

                                                    const statusBadge = item.isLeaveRequest ? {
                                                        label: item.statut === "enAttente" ? t("En attente") :
                                                            item.statut === "approuve" ? t("Approuvé") :
                                                                item.statut === "refuse" ? t("Refusé") :
                                                                    item.statut === "annule" ? t("Annulé") :
                                                                        item.statut === "reporte" ? t("Reporté") : t(item.statut, { defaultValue: item.statut }),
                                                        bg: item.statut === "enAttente" ? "#fef3c7" :
                                                            item.statut === "approuve" ? "#dcfce7" :
                                                                item.statut === "refuse" ? "#fee2e2" :
                                                                    item.statut === "annule" ? "#f3f4f6" :
                                                                        item.statut === "reporte" ? "#dbeafe" : "#f3f4f6",
                                                        color: item.statut === "enAttente" ? "#d97706" :
                                                            item.statut === "approuve" ? "#16a34a" :
                                                                item.statut === "refuse" ? "#ef4444" :
                                                                    item.statut === "annule" ? "#6b7280" :
                                                                        item.statut === "reporte" ? "#2563eb" : "#6b7280",
                                                    } : null;

                                                    return (
                                                        <tr key={item.id || index} style={{
                                                            backgroundColor: isCancelledOrRefused ? "#f9fafb" : isPositive ? "#f0fdf4" : isNegative ? "#fef2f2" : "#fff",
                                                            borderLeft: isCancelledOrRefused ? "3px solid #d1d5db" : isPositive ? "3px solid #16a34a" : isNegative ? "3px solid #ef4444" : "3px solid #6b7280",
                                                            opacity: isCancelledOrRefused ? 0.55 : 1
                                                        }}>
                                                            <td>
                                                                <div style={{fontWeight: 700, color: "#15803d", textDecoration: isCancelledOrRefused ? "line-through" : "none"}}>{item.employeeName || "—"}</div>
                                                                <div style={{fontSize: "11px", color: "#6b7280"}}>{item.employeeCode || "—"}</div>
                                                            </td>
                                                            <td>{formatDate(item.dateMouvement)}</td>
                                                            <td style={{fontSize: "12px", color: "#4b5563", maxWidth: "220px"}}>
                                                                <div style={{fontWeight: "bold", color: "#111827", display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px"}}>
                                                                    <span style={{textDecoration: isCancelledOrRefused ? "line-through" : "none"}}>{item.reference}</span>
                                                                    {statusBadge && (
                                                                        <span style={{
                                                                            fontSize: "10px",
                                                                            padding: "1px 6px",
                                                                            borderRadius: "4px",
                                                                            fontWeight: 600,
                                                                            display: "inline-block",
                                                                            backgroundColor: statusBadge.bg,
                                                                            color: statusBadge.color,
                                                                        }}>
                                                                            {statusBadge.label}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div>{translateMovementReason(item.motif || item.commentaire)}</div>
                                                            </td>
                                                            <td>
                                                                <div className="dcTypeCell">
                                                                    <span className="dcDot" style={{backgroundColor: item.absenceTypeCouleur || (isPositive ? "#16a34a" : "#ef4444")}}/>
                                                                    <span>{translateLeaveType(item.absenceTypeNom || item.typeMouvement)}</span>
                                                                </div>
                                                            </td>
                                                            <td style={{textAlign: "center", fontWeight: 600}}>
                                                                {item.soldeInitialSnapshot !== null && item.soldeInitialSnapshot !== undefined
                                                                    ? `${item.soldeInitialSnapshot} ${t("jours")}`
                                                                    : "—"}
                                                            </td>
                                                            <td style={{textAlign: "center", color: "#ef4444", fontWeight: 700, textDecoration: isCancelledOrRefused ? "line-through" : "none"}}>
                                                                {isNegative ? `${item.valeur} ${t("jours")}` : "—"}
                                                            </td>
                                                            <td style={{ textAlign: "center", fontWeight: 700, color: "#16a34a" }}>
                                                                {isPositive ? `+${item.valeur} ${t("jours")}` : "—"}
                                                            </td>
                                                            <td style={{ textAlign: "center", fontWeight: 800, background: "#f8fafc" }}>
                                                                {item.soldeRestantSnapshot !== null && item.soldeRestantSnapshot !== undefined ? `${item.soldeRestantSnapshot} ${t("jours")}` : "—"}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === "remplacements" && (
                            <div>
                                <h2 className="dcH2">Mes demandes de remplacement</h2>
                                <p className="dcSub">Votre réponse confirme uniquement votre disponibilité et ne valide pas le congé.</p>
                                {replacementTasksLoading ? <div className="dcInfo">Chargement…</div> : replacementTasks.length === 0 ? (
                                    <div className="dcEmpty">Aucune demande de remplacement.</div>
                                ) : replacementTasks.map((item) => (
                                    <div key={item.id} className="dcReplacementCard">
                                        <div><strong style={{ color: "#1e293b" }}>Intérim de {item.employeeName}</strong><br /><span style={{ color: "#64748b", fontSize: 13 }}>{formatDate(item.leaveStartDate)} → {formatDate(item.leaveEndDate)}</span>{item.status === "CANCELLED" && item.cancellationComment && <div style={{ marginTop: 7, color: "#b91c1c", fontSize: 12 }}>Intérim retiré — Motif : {item.cancellationComment}</div>}</div>
                                        <div className="dcReplacementActions"><span style={{ padding: "5px 9px", borderRadius: 999, fontSize: 12, fontWeight: 700, background: item.status === "ACCEPTED" ? "#dcfce7" : item.status === "REFUSED" ? "#fee2e2" : item.status === "PENDING" ? "#fef3c7" : "#e5e7eb", color: item.status === "ACCEPTED" ? "#15803d" : item.status === "REFUSED" ? "#b91c1c" : item.status === "PENDING" ? "#a16207" : "#475569" }}>{item.status === "PENDING" ? "En attente" : item.status === "ACCEPTED" ? "Accepté" : item.status === "REFUSED" ? "Refusé" : item.status === "AUTO_ACCEPTED" ? "Auto-accepté" : "Annulé"}</span>{item.status === "PENDING" && <><button className="dcReplacementAction dcReplacementAction--refuse" type="button" onClick={() => answerReplacement(item.id, false)}><XCircle size={15} />Refuser</button><button className="dcReplacementAction dcReplacementAction--accept" type="button" onClick={() => answerReplacement(item.id, true)}><CheckCircle size={15} />Accepter</button></>}</div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* ===== NOUVELLE DEMANDE ===== */}
                        {activeTab === "demande" && (
                            <div className="dcDemandeForm">
                                <h2 className="dcH2">{t("Formulaire de demande de congé")}</h2>

                                {(paramLoading || balanceLoading || typesCongesDisponibles.length === 0) && (
                                    <div className="dcInfo" style={{marginTop: 10}}>
                                        <Clock size={16}/>
                                        {t("Chargement des types de congés et soldes...")}
                                    </div>
                                )}

                                {soldeError && (
                                    <div className="dcAlert">
                                        <AlertCircle size={18} color="#9f1239" style={{marginTop: 1}}/>
                                        <div>
                                            <h4>{t("Erreur")}</h4>
                                            <p>{soldeError}</p>
                                        </div>
                                    </div>
                                )}

                                <form className="dcForm" onSubmit={handleSubmitDemande}>
                                    {/* --- Section 1 : Type de congé + Commentaire (alignés) --- */}
                                    <div className="dcFormSection">
                                        <div className="dcField" style={{ marginBottom: 14 }}>
                                            <label>{t("Nature de la demande")} <span className="dcReq">*</span></label>
                                            <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                                                <label style={{ display: "flex", alignItems: "center", gap: 7, margin: 0, cursor: "pointer", color: "#334155", fontWeight: 600 }}>
                                                    <input type="radio" name="leave-nature" value="CONGE" checked={formData.nature === "CONGE"} onChange={(e) => setFormData({ ...formData, nature: e.target.value })} disabled={loading} />
                                                    {t("Congé")}
                                                </label>
                                                <label style={{ display: "flex", alignItems: "center", gap: 7, margin: 0, cursor: "pointer", color: "#334155", fontWeight: 600 }}>
                                                    <input type="radio" name="leave-nature" value="ABSENCE" checked={formData.nature === "ABSENCE"} onChange={(e) => setFormData({ ...formData, nature: e.target.value })} disabled={loading} />
                                                    {t("Absence")}
                                                </label>
                                            </div>
                                        </div>
                                        <div className="dcGrid2">
                                            <div className="dcField" style={{ marginBottom: 0 }}>
                                                <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                    <span>{t("Type de congé")} <span className="dcReq">*</span></span>
                                                    {selectedTypeObj && (() => {
                                                        const sObj = findMatchingSolde(formData.absenceTypeId, selectedTypeLabel);
                                                        return sObj ? (
                                                            <span style={{ fontSize: "12px", color: "#2563eb", fontWeight: "600" }}>
                                                                {t("Solde disponible")} : {sObj.restant} {t(sObj.restant > 1 ? "jours" : "jour")}
                                                            </span>
                                                        ) : null;
                                                    })()}
                                                </label>

                                                <select
                                                    className="dcSelect"
                                                    value={formData.absenceTypeId}
                                                    onChange={(e) => {
                                                        setFormData({
                                                            ...formData,
                                                            absenceTypeId: e.target.value,
                                                            justificatif: null,
                                                            autorisation: null,
                                                        });
                                                        setSoldeError("");
                                                    }}
                                                    required
                                                    disabled={loading || paramLoading || balanceLoading || typesCongesDisponibles.length === 0}
                                                >
                                                    <option value="">{t("Sélectionner un type")}</option>

                                                    {typesCongesDisponibles.map((type) => {
                                                        return (
                                                            <option key={type.id} value={type.id}>
                                                                {translateLeaveType(type.label)}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </div>

                                            <div className="dcField" style={{ marginBottom: 0 }}>
                                                <label>{t("Commentaire / Motif du congé (optionnel)")}</label>
                                                <textarea
                                                    className="dcTextarea"
                                                    style={{ minHeight: "42px", height: "42px" }}
                                                    value={formData.commentaire}
                                                    onChange={(e) => setFormData({ ...formData, commentaire: e.target.value })}
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* --- Section 2 : Dates --- */}
                                    <div className="dcFormSection">
                                        <div className="dcFormSectionTitle">
                                            <Calendar size={15} />
                                            {t("Période de l'absence")}
                                        </div>

                                        <div className="dcGrid2">
                                            <div className="dcField">
                                                <label>
                                                    {t("Date de début")} <span className="dcReq">*</span>
                                                </label>
                                                <div style={{ display: "flex", gap: "10px" }}>
                                                    <input
                                                        className="dcInput"
                                                        style={{ flex: 1 }}
                                                        type="date"
                                                        value={formData.dateDebut}
                                                        onChange={(e) => setFormData({...formData, dateDebut: e.target.value})}
                                                        required
                                                        disabled={loading}
                                                    />
                                                    <input
                                                        className="dcInput"
                                                        style={{ width: "125px" }}
                                                        type="time"
                                                        value={formData.heureSortie}
                                                        onChange={(e) => setFormData({ ...formData, heureSortie: e.target.value })}
                                                        disabled={loading}
                                                        aria-label={t("Heure de sortie")}
                                                        title={t("Heure de sortie (optionnelle)")}
                                                    />
                                                    <select
                                                        className="dcSelect"
                                                        style={{ width: "auto", minWidth: "120px" }}
                                                        value={formData.dateDebutType}
                                                        onChange={(e) => setFormData({...formData, dateDebutType: e.target.value})}
                                                        disabled={loading}
                                                    >
                                                        <option value="MATIN">{t("Matin")}</option>
                                                        <option value="APRES_MIDI">{t("Après-midi")}</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="dcField">
                                                <label>{t("Date de fin")} <span className="dcReq">*</span></label>
                                                <div style={{ display: "flex", gap: "10px" }}>
                                                    <input
                                                        className="dcInput"
                                                        style={{ flex: 1 }}
                                                        type="date"
                                                        value={formData.dateFin}
                                                        onChange={(e) => {
                                                            const dateFin = e.target.value;
                                                            const reprise = getReprise(dateFin, formData.dateFinType);
                                                            setFormData({ ...formData, dateFin, dateReprise: reprise.date });
                                                        }}
                                                        required
                                                        disabled={loading}
                                                    />
                                                    <select
                                                        className="dcSelect"
                                                        style={{ width: "auto", minWidth: "120px" }}
                                                        value={formData.dateFinType}
                                                        onChange={(e) => {
                                                            const dateFinType = e.target.value;
                                                            const reprise = getReprise(formData.dateFin, dateFinType);
                                                            setFormData({ ...formData, dateFinType, dateReprise: reprise.date });
                                                        }}
                                                        disabled={loading}
                                                    >
                                                        <option value="MATIN">{t("Matin")}</option>
                                                        <option value="APRES_MIDI">{t("Après-midi")}</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="dcField" style={{ marginTop: "14px", marginBottom: 0 }}>
                                            <label>
                                                {t("Reprise prévue")} <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: "normal" }}>({t("automatique")})</span>
                                            </label>
                                            <div style={{ display: "flex", gap: "10px" }}>
                                                <input
                                                    className="dcInput"
                                                    style={{ flex: 1, backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
                                                    type="date"
                                                    value={formData.dateReprise}
                                                    readOnly
                                                />
                                                <select
                                                    className="dcSelect"
                                                    style={{ width: "auto", minWidth: "120px" }}
                                                    value={formData.dateFinType === "MATIN" ? "APRES_MIDI" : "MATIN"}
                                                    disabled
                                                >
                                                    <option value="MATIN">{t("Matin")}</option>
                                                    <option value="APRES_MIDI">{t("Après-midi")}</option>
                                                </select>
                                            </div>
                                        </div>

                                        {(formData.dateDebut || (formData.dateDebut && formData.dateFin)) && (
                                            <div className="dcSummaryRow">
                                                {formData.dateDebut && (
                                                    <div className="dcSummaryItem">
                                                        <Calendar size={16} color="#2563eb" />
                                                        <div>
                                                            <span className="dcSummaryLabel">{t("Solde disponible au")} {formatDate(formData.dateDebut)}</span>
                                                            <span className="dcSummaryValue">
                                                                {selectedTypeObj
                                                                    ? `${(findMatchingSolde(formData.absenceTypeId, selectedTypeLabel)?.restant ?? 0)} ${t((findMatchingSolde(formData.absenceTypeId, selectedTypeLabel)?.restant ?? 0) > 1 ? "jours" : "jour")}`
                                                                    : `${soldePrincipal ? soldePrincipal.restant : 0} ${t("jours")}`}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}

                                                {formData.dateDebut && formData.dateFin && (
                                                    <div className="dcSummaryItem">
                                                        <Clock size={16} color="#2563eb" />
                                                        <div>
                                                            <span className="dcSummaryLabel">{t("Durée")}</span>
                                                            <span className="dcSummaryValue">{calculerDuree(formData.dateDebut, formData.dateFin)} {t("jours")}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {selectedTypeObj && (selectedTypeObj.periodeDe || selectedTypeObj.periodeA) && (
                                            <div className="dcInfo dcInfo--warn" style={{ marginTop: 12, marginBottom: 0 }}>
                                                <Calendar size={16} />
                                                <span>
                                                    {t("Période de validité")} : <strong>{selectedTypeObj.periodeDe ? `${t("Du")} ${formatDate(selectedTypeObj.periodeDe)}` : ""} {selectedTypeObj.periodeA ? `${t("au")} ${formatDate(selectedTypeObj.periodeA)}` : ""}</strong>
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* --- Section 3 : Remplaçant --- */}
                                    <div className="dcFormSection">
                                        <div className="dcFormSectionTitle">
                                            <RefreshCw size={15} />
                                            {t("Remplaçant / intérim")}
                                        </div>
                                        <p style={{ margin: "0 0 14px", color: "#64748b", fontSize: "12px" }}>
                                            {t("Choisissez un collaborateur de votre service ou d'un autre service.")}
                                        </p>
                                        <div className="dcGrid2">
                                            <div className="dcField" style={{ marginBottom: 0 }}>
                                                <label>{t("Service")}</label>
                                                <select
                                                    className="dcSelect"
                                                    value={formData.remplacantServiceId}
                                                    onChange={(e) => {
                                                        setFormData({ ...formData, remplacantServiceId: e.target.value, remplacantId: "" });
                                                        setRemplacantQuery("");
                                                    }}
                                                    disabled={loading}
                                                >
                                                    <option value="">{t("Tous les services")}</option>
                                                    {servicesRemplacant.map((service) => (
                                                        <option key={service.id} value={service.id}>{service.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="dcField" style={{ marginBottom: 0, position: "relative", zIndex: showRemplacants ? 20 : 1 }}>
                                                <label>{t("Collaborateur remplaçant")}</label>
                                                <input
                                                    className="dcInput"
                                                    type="text"
                                                    value={remplacantQuery}
                                                    placeholder={t("Rechercher par nom ou matricule...")}
                                                    onChange={(e) => {
                                                        setRemplacantQuery(e.target.value);
                                                        setFormData({ ...formData, remplacantId: "" });
                                                        setShowRemplacants(true);
                                                    }}
                                                    onFocus={() => setShowRemplacants(true)}
                                                    onBlur={() => window.setTimeout(() => setShowRemplacants(false), 150)}
                                                    disabled={loading}
                                                    autoComplete="off"
                                                />
                                                {showRemplacants && (
                                                    <div style={{ position: "absolute", left: 0, right: 0, top: "calc(100% + 4px)", zIndex: 30, maxHeight: 208, overflowY: "auto", background: "#fff", border: "1px solid #dbe3ef", borderRadius: 8, boxShadow: "0 8px 18px rgba(15, 23, 42, .12)" }}>
                                                        {remplacantsFiltres.length === 0 ? (
                                                            <div style={{ padding: "10px 12px", color: "#64748b", fontSize: 13 }}>{t("Aucun collaborateur trouvé")}</div>
                                                        ) : remplacantsFiltres.map((user) => {
                                                            const name = user.fullName || `${user.firstName || ""} ${user.lastName || ""}`.trim();
                                                            const service = getServiceDuCollaborateur(user.entityId);
                                                            return (
                                                                <button
                                                                    key={user.id}
                                                                    type="button"
                                                                    onMouseDown={(e) => e.preventDefault()}
                                                                    onClick={() => {
                                                                        setFormData({
                                                                            ...formData,
                                                                            remplacantId: String(user.id),
                                                                            remplacantServiceId: service ? String(service.id) : "",
                                                                        });
                                                                        setRemplacantQuery(`${name}${user.employeeCode ? ` (${user.employeeCode})` : ""}`);
                                                                        setShowRemplacants(false);
                                                                    }}
                                                                    style={{ display: "block", width: "100%", padding: "9px 12px", border: 0, background: "#fff", textAlign: "left", cursor: "pointer", color: "#1e293b" }}
                                                                >
                                                                    <strong style={{ display: "block", fontSize: 13 }}>{name}{user.employeeCode ? ` · ${user.employeeCode}` : ""}</strong>
                                                                    <span style={{ fontSize: 11, color: "#64748b" }}>{service?.name || t("Sans service")}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* --- Section 3 : Pièces jointes --- */}
                                    {(selectedTypeObj?.requireJustificatif || selectedTypeObj?.requireAutorisation) && (
                                        <div className="dcFormSection">
                                            <div className="dcFormSectionTitle">
                                                <FileTextIcon size={15} />
                                                {t("Pièces jointes")}
                                            </div>

                                            <div className="dcAttachGrid">
                                                {selectedTypeObj?.requireJustificatif && (
                                                    <div className="dcAttachCard">
                                                        <div className="dcAttachCard__head">
                                                            <span className="dcAttachCard__label">
                                                                {selectedTypeObj.justificationType}
                                                                {selectedTypeObj.justificatifMandatory && <span className="dcReq"> *</span>}
                                                            </span>
                                                            {selectedTypeObj.justificatifMandatory ? (
                                                                <span className="dcAttachBadge dcAttachBadge--required">{t("Obligatoire")}</span>
                                                            ) : (
                                                                <span className="dcAttachBadge dcAttachBadge--optional">{t("Optionnel")}</span>
                                                            )}
                                                        </div>

                                                        {!formData.justificatif ? (
                                                            <label htmlFor="dc-justif" className="dcDropzone">
                                                                <Upload size={22} color="#9ca3af" />
                                                                <span className="dcDropzone__text">{t("Glissez-déposez ou")} <strong>{t("parcourez")}</strong></span>
                                                                <span className="dcDropzone__hint">{t("PDF, JPG, PNG (max 5Mo)")}</span>
                                                                <input
                                                                    type="file"
                                                                    className="dcHiddenFile"
                                                                    id="dc-justif"
                                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                                    onChange={(e) => setFormData({ ...formData, justificatif: e.target.files?.[0] || null })}
                                                                    disabled={loading}
                                                                />
                                                            </label>
                                                        ) : (
                                                            <div className="dcFileChip">
                                                                <FileTextIcon size={18} color="#204385" />
                                                                <span className="dcFileChip__name" title={formData.justificatif.name}>{formData.justificatif.name}</span>
                                                                <button
                                                                    type="button"
                                                                    className="dcFileChip__remove"
                                                                    title={t("Retirer le fichier")}
                                                                    onClick={() => setFormData({ ...formData, justificatif: null })}
                                                                    disabled={loading}
                                                                >
                                                                    <X size={14} />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {selectedTypeObj?.requireAutorisation && (
                                                    <div className="dcAttachCard">
                                                        <div className="dcAttachCard__head">
                                                            <span className="dcAttachCard__label">
                                                                {t("Autorisation")} <span className="dcReq">*</span>
                                                            </span>
                                                            <span className="dcAttachBadge dcAttachBadge--required">{t("Obligatoire")}</span>
                                                        </div>

                                                        {!formData.autorisation ? (
                                                            <label htmlFor="dc-auto" className="dcDropzone dcDropzone--accent">
                                                                <FileTextIcon size={22} color="#ee645f" />
                                                                <span className="dcDropzone__text">{t("Document d'autorisation")} (<strong>{t("RH, manager...")}</strong>)</span>
                                                                <span className="dcDropzone__hint">{t("PDF, JPG, PNG (max 5Mo)")}</span>
                                                                <input
                                                                    type="file"
                                                                    className="dcHiddenFile"
                                                                    id="dc-auto"
                                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                                    onChange={(e) => setFormData({ ...formData, autorisation: e.target.files?.[0] || null })}
                                                                    disabled={loading}
                                                                />
                                                            </label>
                                                        ) : (
                                                            <div className="dcFileChip">
                                                                <FileTextIcon size={18} color="#ee645f" />
                                                                <span className="dcFileChip__name" title={formData.autorisation.name}>{formData.autorisation.name}</span>
                                                                <button
                                                                    type="button"
                                                                    className="dcFileChip__remove"
                                                                    title={t("Retirer le fichier")}
                                                                    onClick={() => setFormData({ ...formData, autorisation: null })}
                                                                    disabled={loading}
                                                                >
                                                                    <X size={14} />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div className="dcFormFooter">
                                        <button
                                            type="button"
                                            className="dcBtn dcBtn--ghost"
                                            onClick={() => {
                                                setFormData({
                                                    absenceTypeId: "",
                                                    nature: "CONGE",
                                                    heureSortie: "",
                                                    dateDebut: "",
                                                    dateDebutType: "MATIN",
                                                    dateFin: "",
                                                    dateFinType: "APRES_MIDI",
                                                    dateReprise: "",
                                                    commentaire: "",
                                                    justificatif: null,
                                                    autorisation: null,
                                                    demiJourneeDebut: false,
                                                    demiJourneeDebutType: "MATIN",
                                                    demiJourneeFin: false,
                                                    demiJourneeFinType: "MATIN",
                                                    remplacantId: "",
                                                    remplacantServiceId: "",
                                                });
                                                setRemplacantQuery("");
                                                setSoldeError("");
                                            }}
                                            disabled={loading}
                                        >
                                            {t("Réinitialiser")}
                                        </button>

                                        <button
                                            type="submit"
                                            className="dcBtn dcBtn--primary"
                                            disabled={loading || paramLoading || balanceLoading || !formData.absenceTypeId}
                                        >
                                            {loading ? t("Envoi...") : t("Soumettre la demande")}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* ===== HISTORIQUE ===== */}
                        {activeTab === "historique" && (
                            <div>
                                <h2 className="dcH2" style={{ marginBottom: 12 }}>
                                    {t("Historique des demandes")}
                                </h2>

                                <div className="dcTableWrap">
                                    <table className="dcTable">
                                        <thead>
                                        <tr>
                                            <th>{t("Type")}</th>
                                            <th>{t("Période")}</th>

                                            <th>{t("Durée")}</th>
                                            <th>{t("Date de la demande")}</th>
                                            <th>{t("Statut")}</th>
                                            <th>{t("Suivi")}</th>
                                            <th>{t("Actions")}</th>
                                        </tr>
                                        </thead>

                                        <tbody>
                                        {loading && historiqueDemandes.length === 0 ? (
                                            <tr>
                                                <td colSpan={7} style={{ padding: 16 }}>
                                                    <div className="dcEmpty" style={{ minHeight: 140 }}>
                                                        <Clock size={28} />
                                                        <div>{t("Chargement...")}</div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : historiqueDemandes.length === 0 ? (
                                            <tr>
                                                <td colSpan={7} style={{ padding: 16 }}>
                                                    <div className="dcEmpty" style={{ minHeight: 140 }}>
                                                        <FileText size={28} />
                                                        <div>{t("Aucune demande trouvée")}</div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            historyRows.map((row) => (
                                                <React.Fragment key={`leave-row-${row.key}`}>
                                                    {row.folder && <tr className="dcLeaveFolderHeader"><td colSpan={7} style={{ padding: "7px 12px" }}><button type="button" onClick={() => toggleLeaveFolder(row.key)} style={{ border: 0, background: "transparent", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, color: "#1e3a5f", fontWeight: 700, fontSize: 12 }}><ChevronDown size={16} style={{ transform: closedLeaveFolders.has(row.key) ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform .15s" }} />Dossier de congé DC-{String(row.key).padStart(6, "0")} · {row.requests.length} demandes</button></td></tr>}
                                                    {(!row.folder || !closedLeaveFolders.has(row.key)) && row.requests.map((demande, index) => (
                                                    <tr key={demande.id} className={row.folder ? "dcLeaveFolderItem" : ""}>
                                                        <td>
                                                            <div className="dcTypeCell">
                                                                <span className="dcDot" style={{ backgroundColor: demande.couleurType }} />
                                                                <span style={{ fontWeight: 600, color: "#204385" }}>{row.folder ? `${index === 0 ? "Demande principale" : "Demande complémentaire"} · ${translateLeaveType(demande.typeConge)}` : translateLeaveType(demande.typeConge)}</span>
                                                            </div>
                                                        </td>

                                                        <td>
                                                            <div>
                                                                <div className="dcSmall">{formatDate(demande.dateDebut)}</div>
                                                                {demande.dateDebut !== demande.dateFin && <div className="dcTiny">{t("au")} {formatDate(demande.dateFin)}</div>}
                                                                {demande.dateReprise && <div className="dcTiny" style={{ color: "#059669", fontWeight: 500 }}>{t("Reprise le")} {formatDate(demande.dateReprise)}</div>}
                                                            </div>
                                                        </td>

                                                        <td style={{ fontWeight: 700, color: "#204385" }}>{demande.duree} {t("jours")}</td>
                                                        <td className="dcSmall">{formatDate(demande.datesDemande)}</td>
                                                        <td><span className="dcBadge" style={{ background: "#eef2ff", color: "#3730a3" }}>{t(getRequestStatusLabel({ ...demande, status: demande.rawStatus }))}</span></td>
                                                        <td className="dcSmall"><span className="dcTiny">{getRequestTracking({ ...demande, status: demande.rawStatus }).label}</span>{demande.replacement && <><div style={{ marginTop: 5 }}><span style={{ padding: "3px 7px", borderRadius: 999, fontSize: 10, fontWeight: 700, background: demande.replacement.status === "ACCEPTED" ? "#dcfce7" : demande.replacement.status === "REFUSED" || demande.replacement.status === "CANCELLED" ? "#fee2e2" : "#fef3c7", color: demande.replacement.status === "ACCEPTED" ? "#15803d" : demande.replacement.status === "REFUSED" || demande.replacement.status === "CANCELLED" ? "#b91c1c" : "#a16207" }}>Intérim : {demande.replacement.status === "ACCEPTED" ? "Accepté" : demande.replacement.status === "REFUSED" ? "Refusé" : demande.replacement.status === "PENDING" ? "En attente" : demande.replacement.status === "AUTO_ACCEPTED" ? "Auto-accepté" : "Retiré"}</span></div>{demande.replacement.status === "CANCELLED" && demande.replacement.cancellationComment && <div style={{ marginTop: 4, color: "#b91c1c", fontSize: 11 }}>Motif : {demande.replacement.cancellationComment}</div>}</>}{demande.hasReplacementHistory && <button type="button" onClick={() => showReplacementHistory(demande.id)} style={{ marginTop: 5, border: 0, background: "transparent", color: "#2563eb", padding: 0, fontSize: 11, cursor: "pointer" }}>Voir l'historique</button>}</td>

                                                        <td>
                                                            <div className="dcActions">
                                                                {demande.statut === "refuse" && demande.motifRejet && (
                                                                    <button
                                                                        className="dcIconBtn"
                                                                        title={t("Voir motif")}
                                                                        type="button"
                                                                        onClick={() => setSelectedDemande(demande)}
                                                                    >
                                                                        <Eye size={16} />
                                                                    </button>
                                                                )}

                                                                {demande.statut === "enAttente" && canCancelLeaveRequest(demande) && (
                                                                    <button
                                                                        className="dcIconBtn is-danger"
                                                                        title={t("Annuler")}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setSelectedDemande(demande);
                                                                            setShowAnnulationModal(true);
                                                                        }}
                                                                    >
                                                                        <X size={16} />
                                                                    </button>
                                                                )}

                                                                {demande.statut === "approuve" && (
                                                                    <>
                                                                        {canCancelLeaveRequest(demande) && (
                                                                            <button
                                                                                className="dcIconBtn is-danger"
                                                                                title={t("Annuler")}
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setSelectedDemande(demande);
                                                                                    setShowAnnulationModal(true);
                                                                                }}
                                                                            >
                                                                                <XCircle size={16} />
                                                                            </button>
                                                                        )}
                                                                        <button
                                                                            className="dcIconBtn is-primary"
                                                                            title={t("Reporter")}
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setSelectedDemande(demande);
                                                                                setShowReportModal(true);
                                                                            }}
                                                                        >
                                                                            <RefreshCw size={16} />
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    ))}
                                                </React.Fragment>
                                            ))
                                        )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {replacementHistoryFor && (
                            <div role="presentation" onMouseDown={() => setReplacementHistoryFor(null)} style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)", zIndex: 10020, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
                                <div role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()} style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 680, maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 25px -5px rgba(0,0,0,.1), 0 8px 10px -6px rgba(0,0,0,.1)", overflow: "hidden" }}>
                                    <div style={{ padding: "16px 24px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f8fafc" }}><div style={{ display: "flex", alignItems: "center", gap: 10 }}><div style={{ width: 36, height: 36, borderRadius: 10, background: "#20438515", display: "flex", alignItems: "center", justifyContent: "center", color: "#204385" }}><RefreshCw size={20} /></div><div><h3 style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", margin: 0 }}>Historique de l'intérim</h3><span style={{ fontSize: 12, color: "#64748b" }}>Décisions et modifications liées au remplaçant</span></div></div><button type="button" onClick={() => setReplacementHistoryFor(null)} style={{ background: "#f1f5f9", border: "none", cursor: "pointer", color: "#64748b", padding: 6, borderRadius: 8, display: "flex" }}><X size={18} /></button></div>
                                    <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>{replacementHistory.length === 0 ? <div className="dcEmpty">Aucun événement.</div> : replacementHistory.map((entry, index) => {
                                        const isRefusal = entry.newStatus === "REFUSED";
                                        const previousEntry = replacementHistory[index - 1];
                                        // Les anciens historiques avaient le motif sur l'annulation de l'ancien remplaçant.
                                        // On l'affiche sous la nouvelle désignation, comme pour les nouvelles données.
                                        const designationReason = entry.action === "DESIGNATION" && previousEntry?.action === "REMPLACEMENT_MODIFIE" ? previousEntry.comment : null;
                                        const visibleComment = entry.action === "REMPLACEMENT_MODIFIE" ? null : (entry.comment || designationReason);
                                        const label = entry.action === "DESIGNATION" ? `${entry.actorName} a désigné ${entry.newReplacementName}.` : entry.action === "DECISION_REMPLACANT" ? (isRefusal ? `${entry.actorName} a refusé l'intérim.` : `${entry.actorName} a accepté l'intérim.`) : entry.action === "REMPLACEMENT_MODIFIE" ? `${entry.actorName} a retiré ${entry.previousReplacementName} de l'intérim.` : entry.action === "CONGE_REFUSE" ? "L'intérim a été annulé car le congé a été refusé." : entry.action === "CONGE_ANNULE" ? "L'intérim a été annulé car le congé a été annulé." : entry.action === "AUTO_ACCEPTATION" ? "L'intérim a été auto-accepté à la date de début." : `${entry.actorName} a retiré le remplaçant.`;
                                        return <div key={entry.id} style={{ padding: "12px 0", borderBottom: "1px solid #eef2f7" }}><div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}><strong style={{ color: isRefusal ? "#b91c1c" : "#1e3a5f", fontSize: 13 }}>{label}</strong><span style={{ flexShrink: 0, fontSize: 11, color: "#94a3b8" }}>{formatDate(entry.createdAt)}</span></div>{entry.previousReplacementName || entry.newReplacementName ? <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>{entry.previousReplacementName || "—"} → {entry.newReplacementName || "—"}</div> : null}{visibleComment && <div style={{ marginTop: 7, padding: "8px 10px", background: isRefusal ? "#fef2f2" : "#f8fafc", borderRadius: 7, color: "#334155", fontSize: 12 }}><b>{isRefusal ? "Motif du refus" : "Motif de la désignation"} :</b> {visibleComment}</div>}</div>;
                                    })}</div>
                                    <div style={{ padding: "14px 24px", borderTop: "1px solid #e5e7eb", background: "#f8fafc", display: "flex", justifyContent: "flex-end" }}><button type="button" className="dcBtn dcBtn--ghost" onClick={() => setReplacementHistoryFor(null)}>Fermer</button></div>
                                </div>
                            </div>
                        )}

                        {/* ===== ✅ CALENDRIER AMÉLIORÉ ===== */}
                        {activeTab === "agenda" && (
                            <div className="dcAgenda">
                                <div className="dcAgenda__intro">
                                    <div>
                                        <h2 className="dcH2">{t("Mon agenda de congés")}</h2>
                                        <p className="dcSub">{t("Retrouvez toutes vos demandes, avec leur statut à chaque date.")}</p>
                                    </div>
                                    <div className="dcAgenda__nav" aria-label={t("Navigation du calendrier")}>
                                        <button type="button" className="dcAgenda__navButton" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} title={t("Mois précédent")}>
                                            <ChevronLeft size={18} />
                                        </button>
                                        <button type="button" className="dcAgenda__today" onClick={() => setCurrentMonth(new Date())}>{t("Aujourd’hui")}</button>
                                        <button type="button" className="dcAgenda__navButton" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} title={t("Mois suivant")}>
                                            <ChevronRight size={18} />
                                        </button>
                                    </div>
                                </div>

                                <div className="dcAgenda__month">{formatMonth(currentMonth)}</div>
                                <div className="dcAgenda__calendar">
                                    <div className="dcAgenda__weekdays">
                                        {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((day) => <div key={day}>{t(day)}</div>)}
                                    </div>
                                    <div className="dcAgenda__grid">
                                        {getMonthData(currentMonth).flat().map((date, index) => {
                                            const dateKey = date && `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
                                            const publicHolidayName = date ? getPublicHolidayName(date) : "";
                                            const nonWorkingDay = date && isNonWorkingDay(date);
                                            const leavesForDay = date ? historiqueDemandes.filter((demande) => {
                                                const start = String(demande.dateDebut || "").slice(0, 10);
                                                const end = String(demande.dateFin || "").slice(0, 10);
                                                return start && end && dateKey >= start && dateKey <= end;
                                            }) : [];
                                            const isToday = date && date.toDateString() === new Date().toDateString();

                                            return (
                                                <div
                                                    key={dateKey || `empty-${index}`}
                                                    className={`dcAgenda__day ${!date ? "is-empty" : ""} ${isToday ? "is-today" : ""} ${publicHolidayName ? "is-holiday" : ""} ${nonWorkingDay && !publicHolidayName ? "is-non-working" : ""}`}
                                                    title={publicHolidayName || (nonWorkingDay ? t("Jour non ouvré") : "")}
                                                >
                                                    {date && <>
                                                        <span className="dcAgenda__date">{date.getDate()}</span>
                                                        <div className="dcAgenda__events">
                                                            {publicHolidayName && (
                                                                <div className="dcAgenda__event is-holiday" title={publicHolidayName}>
                                                                    <Calendar size={12} />
                                                                    <span>{publicHolidayName}</span>
                                                                </div>
                                                            )}
                                                            {nonWorkingDay && !publicHolidayName && (
                                                                <div className="dcAgenda__event is-non-working" title={t("Jour non ouvré")}>
                                                                    <Clock size={12} />
                                                                    <span>{t("Non ouvré")}</span>
                                                                </div>
                                                            )}
                                                            {leavesForDay.map((demande) => {
                                                                const status = {
                                                                    enAttente: { label: t("En attente"), className: "is-pending", icon: Clock },
                                                                    approuve: { label: t("Approuvé"), className: "is-approved", icon: CheckCircle },
                                                                    refuse: { label: t("Refusé"), className: "is-refused", icon: XCircle },
                                                                    annule: { label: t("Annulé"), className: "is-cancelled", icon: XCircle },
                                                                    reporte: { label: t("Reporté"), className: "is-postponed", icon: RefreshCw },
                                                                    reporte_en_attente: { label: t("En attente de report"), className: "is-pending", icon: RefreshCw },
                                                                }[demande.statut] || { label: t("En attente"), className: "is-pending", icon: Clock };
                                                                const StatusIcon = status.icon;
                                                                return <div key={demande.id} className={`dcAgenda__event ${status.className}`} title={`${translateLeaveType(demande.typeConge)} — ${status.label}`}>
                                                                    <StatusIcon size={12} />
                                                                    <span>{translateLeaveType(demande.typeConge)}</span>
                                                                </div>;
                                                            })}
                                                        </div>
                                                    </>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="dcAgenda__legend">
                                    <span className="dcAgenda__legendTitle">{t("Statuts")}</span>
                                    <span className="dcAgenda__legendItem is-pending"><Clock size={13} /> {t("En attente")}</span>
                                    <span className="dcAgenda__legendItem is-approved"><CheckCircle size={13} /> {t("Approuvé")}</span>
                                    <span className="dcAgenda__legendItem is-refused"><XCircle size={13} /> {t("Refusé")}</span>
                                    <span className="dcAgenda__legendItem is-cancelled"><XCircle size={13} /> {t("Annulé")}</span>
                                    <span className="dcAgenda__legendItem is-postponed"><RefreshCw size={13} /> {t("Reporté")}</span>
                                    <span className="dcAgenda__legendItem is-holiday"><Calendar size={13} /> {t("Jour férié")}</span>
                                    <span className="dcAgenda__legendItem is-non-working"><Clock size={13} /> {t("Jour non ouvré")}</span>
                                </div>
                            </div>
                        )}

                    </div>
                </div>

                {/* ===== MODALS (inchangés) ===== */}
                {showAnnulationModal && selectedDemande && (
                    <div className="dcModal">
                        <div className="dcModalPanel">
                            <div className="dcModalHeader">
                                <div>
                                    <h3 className="dcModalTitle">{t("Annuler la demande")}</h3>
                                    <div className="dcModalSub">{t("Votre solde sera restauré")}</div>
                                </div>
                                <button
                                    className="dcXBtn"
                                    type="button"
                                    onClick={() => {
                                        setShowAnnulationModal(false);
                                        setSelectedDemande(null);
                                    }}
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <div className="dcModalBody">
                                <div className="dcBox" style={{ boxShadow: "none" }}>
                                    <div className="dcTypeCell" style={{ marginBottom: 6 }}>
                                        <span className="dcDot" style={{ backgroundColor: selectedDemande.couleurType }} />
                                        <span style={{ fontWeight: 800, color: "#204385" }}>{translateLeaveType(selectedDemande.typeConge)}</span>
                                    </div>
                                    <div className="dcSmall">
                                        {t("Du")} {formatDate(selectedDemande.dateDebut)} {t("au")} {formatDate(selectedDemande.dateFin)}
                                    </div>
                                    <div className="dcSmall">{t("Durée")} : {selectedDemande.duree} {t("jours")}</div>
                                </div>

                                <div className="dcWarnBox" style={{ marginTop: 12 }}>
                                    <strong>{t("Note")} :</strong> {t("Le solde sera automatiquement restauré après annulation.")}
                                </div>
                            </div>

                            <div className="dcModalFooter">
                                <button
                                    className="dcBtn dcBtn--ghost"
                                    type="button"
                                    onClick={() => {
                                        setShowAnnulationModal(false);
                                        setSelectedDemande(null);
                                    }}
                                >
                                    {t("Conserver")}
                                </button>
                                <button className="dcBtn dcBtn--danger" type="button" onClick={handleAnnulerDemande}>
                                    {t("Confirmer l'annulation")}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showJustificatifModal && (
                    <div className="dcModal">
                        <div className="dcModalPanel dcModalPanel--sm">
                            <div className="dcModalHeader">
                                <div>
                                    <h3 className="dcModalTitle">{t("Ajouter un justificatif")}</h3>
                                    <div className="dcModalSub">{t("Téléversement du document")}</div>
                                </div>
                                <button
                                    className="dcXBtn"
                                    type="button"
                                    onClick={() => {
                                        setShowJustificatifModal(false);
                                        setJustificatifUpload({ demandeId: null, fichier: null });
                                    }}
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <form onSubmit={handleUploadJustificatif}>
                                <div className="dcModalBody">
                                    <div className="dcUploadBox">
                                        <Upload size={34} color="#9ca3af" />
                                        <div className="dcUploadHint">{t("Glissez-déposez ou cliquez")}</div>

                                        <input
                                            type="file"
                                            id="dc-modal-justif"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={(e) => setJustificatifUpload({ ...justificatifUpload, fichier: e.target.files?.[0] || null })}
                                            required
                                        />

                                        <label htmlFor="dc-modal-justif" className="dcBtn dcBtn--primary" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                            <Upload size={16} />
                                            {t("Parcourir")}
                                        </label>

                                        {justificatifUpload.fichier && <div className="dcUploadOk">✓ {justificatifUpload.fichier.name}</div>}
                                    </div>

                                    <div className="dcHelp">{t("PDF, JPG, PNG (max 5Mo)")}</div>
                                </div>

                                <div className="dcModalFooter">
                                    <button
                                        type="button"
                                        className="dcBtn dcBtn--ghost"
                                        onClick={() => {
                                            setShowJustificatifModal(false);
                                            setJustificatifUpload({ demandeId: null, fichier: null });
                                        }}
                                    >
                                        {t("Annuler")}
                                    </button>
                                    <button type="submit" className="dcBtn dcBtn--primary" disabled={!justificatifUpload.fichier}>
                                        {t("Télécharger")}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {selectedDemande &&
                    !showAnnulationModal &&
                    activeTab === "historique" &&
                    selectedDemande.statut === "refuse" &&
                    selectedDemande.motifRejet && (
                        <div className="dcModal">
                            <div className="dcModalPanel dcModalPanel--sm">
                                <div className="dcModalHeader">
                                    <div>
                                        <h3 className="dcModalTitle">{t("Motif de rejet")}</h3>
                                        <div className="dcModalSub">{t("Détails")}</div>
                                    </div>
                                    <button className="dcXBtn" type="button" onClick={() => setSelectedDemande(null)}>
                                        <X size={18} />
                                    </button>
                                </div>

                                <div className="dcModalBody">
                                    <div className="dcBox" style={{ boxShadow: "none" }}>
                                        <div className="dcTypeCell" style={{ marginBottom: 6 }}>
                                            <span className="dcDot" style={{ backgroundColor: selectedDemande.couleurType }} />
                                            <span style={{ fontWeight: 800, color: "#204385" }}>{translateLeaveType(selectedDemande.typeConge)}</span>
                                        </div>
                                        <div className="dcSmall">
                                            {t("Du")} {formatDate(selectedDemande.dateDebut)} {t("au")} {formatDate(selectedDemande.dateFin)}
                                        </div>
                                        <div className="dcWarnBox" style={{ marginTop: 12 }}>
                                            {selectedDemande.motifRejet}
                                        </div>
                                    </div>
                                </div>

                                <div className="dcModalFooter">
                                    <button className="dcBtn dcBtn--primary" type="button" onClick={() => setSelectedDemande(null)}>
                                        {t("Fermer")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
            </div>
            {/* MODALE DE REPORT (RE-PLANIFIER) */}
            {showReportModal && selectedDemande && (
                <ReportModal
                    demande={selectedDemande}
                    onClose={() => {
                        setShowReportModal(false);
                        setSelectedDemande(null);
                    }}
                    onConfirm={(dates) => {
                        dispatch(postponeLeaveRequestRequest({
                            id: selectedDemande.id,
                            startDate: dates.startDate,
                            endDate: dates.endDate
                        }));
                        setShowReportModal(false);
                        setSelectedDemande(null);
                    }}
                />
            )}
        </div>
    );
};

// ===================== ✅ COMPOSANTS INTERNES =====================

const ReportModal = ({ demande, onClose, onConfirm }) => {
    const { t, i18n } = useTranslation();
    const [newStartDate, setNewStartDate] = useState("");
    const [newEndDate, setNewEndDate] = useState("");
    const [error, setError] = useState("");

    const oldDuration = useMemo(() => {
        if (!demande.dateDebut || !demande.dateFin) return 0;
        const d1 = new Date(demande.dateDebut);
        const d2 = new Date(demande.dateFin);
        return Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24)) + 1;
    }, [demande]);

    const currentDuration = useMemo(() => {
        if (!newStartDate || !newEndDate) return 0;
        const d1 = new Date(newStartDate);
        const d2 = new Date(newEndDate);
        if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return 0;

        // Calcul identique au backend (différence de jours + 1)
        const diffTime = d2.getTime() - d1.getTime();
        const diffDays = Math.round(diffTime / (1000 * 3600 * 24));
        return diffDays + 1;
    }, [newStartDate, newEndDate]);

    // Aide pour l'utilisateur : ajuster automatiquement la date de fin
    const autoAdjustEndDate = () => {
        if (!newStartDate || oldDuration <= 0) return;
        const d1 = new Date(newStartDate);
        const d2 = new Date(d1);
        d2.setDate(d1.getDate() + oldDuration - 1);
        setNewEndDate(d2.toISOString().split("T")[0]);
        setError("");
    };


    const handleConfirm = () => {
        setError("");

        if (!newStartDate || !newEndDate) {
            setError(t("Veuillez sélectionner les deux dates."));
            return;
        }

        const d1 = new Date(newStartDate);
        const d2 = new Date(newEndDate);
        const oldD1 = new Date(demande.dateDebut);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (d1 < today) {
            setError(t("La nouvelle date de début ne peut pas être dans le passé."));
            return;
        }

        if (d1 <= oldD1) {
            setError(`${t("La nouvelle date de début doit être strictly postérieure à l'ancienne")} (${oldD1.toLocaleDateString(i18n.language)}).`);
            return;
        }

        if (d2 < d1) {
            setError(t("La date de fin ne peut pas être antérieure à la date de début."));
            return;
        }

        onConfirm({ startDate: newStartDate, endDate: newEndDate });
    };

    return (
        <div className="dcModal">
            <div className="dcModalPanel dcModalPanel--sm">
                <div className="dcModalHeader">
                    <div>
                        <div className="dcModalTitle">
                            <RefreshCw size={16} style={{ marginRight: 8, verticalAlign: "middle" }} />
                            {t("Reporter la demande")}
                        </div>
                        <div className="dcModalSub">{t("Sélectionnez la nouvelle période de congé")} ({t("Durée initiale")} : {oldDuration} {t("jours")})</div>
                    </div>
                    <button className="dcXBtn" type="button" onClick={onClose}><X size={18} /></button>
                </div>

                <div className="dcModalBody">
                    <div className="dcWarnBox" style={{ marginBottom: 20 }}>
                        {t("Ancienne période")} : <b>{new Date(demande.dateDebut).toLocaleDateString(i18n.language)}</b> {t("au")} <b>{new Date(demande.dateFin).toLocaleDateString(i18n.language)}</b>
                    </div>

                    <div className="dcGrid2">
                        <div className="dcField">
                            <label>{t("Nouvelle date de début")}</label>
                            <input
                                type="date"
                                value={newStartDate}
                                onChange={(e) => {
                                    setNewStartDate(e.target.value);
                                    setError("");
                                }}
                                className="dcInput"
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="dcField">
                            <label>{t("Nouvelle date de fin")}</label>
                            <div style={{ display: "flex", gap: 8 }}>
                                <input
                                    type="date"
                                    value={newEndDate}
                                    onChange={(e) => {
                                        setNewEndDate(e.target.value);
                                        setError("");
                                    }}
                                    className="dcInput"
                                    style={{ flex: 1 }}
                                />
                                <button
                                    type="button"
                                    className="dcIconBtn"
                                    title={t("Ajuster selon la durée originale")}
                                    onClick={autoAdjustEndDate}
                                    style={{ background: "#f0f9ff", border: "1px solid #bae6fd" }}
                                >
                                    <TrendingUp size={16} color="#0369a1" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="dcAlert" style={{ marginTop: 15 }}>
                            <AlertCircle size={16} color="#ef4444" />
                            <div style={{ color: "#9f1239", fontSize: "12px" }}>{error}</div>
                        </div>
                    )}

                    <div className="dcInfo" style={{ marginTop: 20, backgroundColor: "#eff6ff", borderColor: "#dbeafe" }}>
                        <Clock size={16} color="#3b82f6" />
                        <span>
                            {t("Durée sélectionnée")} : <b>{currentDuration} {t("jours")}</b>
                            {oldDuration > 0 && ` (${t("Durée initiale")} : ${oldDuration} ${t("jours")})`}
                        </span>
                    </div>

                </div>

                <div className="dcModalFooter">
                    <button className="dcBtn dcBtn--ghost" type="button" onClick={onClose}>{t("Annuler")}</button>
                    <button
                        className="dcBtn dcBtn--primary"
                        type="button"
                        onClick={handleConfirm}
                        disabled={currentDuration <= 0}
                    >
                        {t("Confirmer le report")}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DemandeConges;
